/**
 * 6-SYSTEM FINANCIAL DATA ARCHITECTURE
 * 
 * Company: BuildRight Construction Ltd
 * Location: Leicester, UK
 * Employees: 50 (REVISED from 150)
 * Founded: January 2010 (15 years operating)
 * Financial Year: April 2025 - March 2026
 * 
 * FINANCIAL PROFILE (REVISED):
 * - Annual Turnover: £5,000,000
 * - Annual Gross Profit: £900,000 (18% margin)
 * - Annual Operating Expenses: £760,000
 * - Annual Net Profit: £140,000 (2.8% margin)
 * 
 * MONTHLY AVERAGES:
 * - Monthly Revenue: £416,667
 * - Monthly Gross Profit: £75,000
 * - Monthly Operating Expenses: £63,333
 * - Monthly Net Profit: £11,667
 * 
 * CASH POSITION (as of 5 Feb 2026):
 * - Total Cash: £247,000
 * - Coverage: 3.9 months of operating expenses (£247K ÷ £63K)
 * 
 * Generates realistic, interlinked financial data across 6 systems:
 * 1. Accounting System (60% of forecast)
 * 2. Payroll System (20% of forecast)
 * 3. Time Recording System (5% of forecast)
 * 4. Purchase & Sales Orders (5% of forecast)
 * 5. CRM Pipeline (10% of forecast)
 * 6. Budget System (strategic, beyond 90 days)
 * 
 * Date Baseline: February 5, 2026
 * Historical: December 7, 2025 → February 5, 2026 (past 60 days)
 * Bank History: February 2024 → February 5, 2026 (2 years)
 * Future Forecast: February 6, 2026 → May 5, 2026 (90 days)
 * Strategic Budget: May 2026 → February 2029
 */

import { TransactionCategory, ConfidenceLevel, InvoiceStatus, BillStatus, BankAccount, CreditFacility, RestrictedFund, ManualFinancingFacility } from '../types';
import { formatDate } from '@/lib/formatting';

// ============================================================================
// DATE CONSTANTS - DYNAMIC (always relative to current date)
// ============================================================================

export const TODAY = new Date();
TODAY.setHours(0, 0, 0, 0); // Normalize to start of day

// Historical data range (60 days back)
export const HISTORICAL_START = new Date(TODAY.getTime() - (60 * 24 * 60 * 60 * 1000));
HISTORICAL_START.setHours(0, 0, 0, 0);

// Bank history (2 years back)
export const BANK_HISTORY_START = new Date(TODAY.getFullYear() - 2, TODAY.getMonth(), 1);

// Forecast range (90 days ahead)
export const FORECAST_END = new Date(TODAY.getTime() + (90 * 24 * 60 * 60 * 1000));
FORECAST_END.setHours(0, 0, 0, 0);

// Strategic budget (3 years ahead)
export const STRATEGIC_BUDGET_END = new Date(TODAY.getFullYear() + 3, TODAY.getMonth(), 28);

// Current month boundaries
export const START_OF_CURRENT_MONTH = new Date(TODAY.getFullYear(), TODAY.getMonth(), 1);
export const END_OF_CURRENT_MONTH = new Date(TODAY.getFullYear(), TODAY.getMonth() + 1, 0);

// Date calculation helpers
export const DATE_60_DAYS_AGO = new Date(TODAY.getTime() - (60 * 24 * 60 * 60 * 1000));
export const DATE_90_DAYS_AGO = new Date(TODAY.getTime() - (90 * 24 * 60 * 60 * 1000));

// ============================================================================
// COMPANY FINANCIAL CONSTANTS
// ============================================================================

// Annual figures (Financial Year: April 2025 - March 2026) - REVISED PROFILE
export const ANNUAL_TURNOVER = 5_000_000;
export const ANNUAL_GROSS_PROFIT = 900_000; // 18% margin
export const ANNUAL_OPERATING_EXPENSES = 760_000;
export const ANNUAL_NET_PROFIT = 140_000; // 2.8% margin

// Monthly averages (derived from annual)
export const MONTHLY_REVENUE = 416_667; // £5M ÷ 12
export const MONTHLY_GROSS_PROFIT = 75_000; // £900K ÷ 12
export const MONTHLY_OPERATING_EXPENSES = 63_333; // £760K ÷ 12
export const MONTHLY_NET_PROFIT = 11_667; // £140K ÷ 12

// Monthly expense breakdown (totals to £63,333)
// Note: Values scaled from requirements to hit target total
export const MONTHLY_EXPENSES = {
  // Direct project costs (38% + 25% + 8% = 71% of variable costs)
  materials: 20_100, // 38%
  subcontractors: 13_400, // 25%
  equipmentHire: 4_200, // 8%
  
  // Labor costs (32% + 3.8% + 1.7% = 37.5% of variable costs)
  payroll: 16_750, // Wages & salaries
  employerNI: 2_010, // Employer National Insurance
  pension: 920, // Pension contributions
  
  // Operating expenses (fixed costs)
  officeRent: 1_005,
  utilities: 335,
  insurance: 545,
  vehicleCosts: 670,
  softwareIT: 377,
  professionalFees: 419,
  marketing: 251,
  sundries: 670,
  
  // Tax provisions
  vatQuarterly: 1_072, // VAT (quarterly, averaged monthly)
  corporationTax: 628, // Corporation tax (reserved monthly)
};

// Cash position (as of 5 Feb 2026)
export const TARGET_CASH_POSITION = 247_038.50;
export const MINIMUM_CASH_THRESHOLD = 50_000;
export const TARGET_COVERAGE_MONTHS = 7; // months of operating expenses

// ============================================================================
// CONFIDENCE LEVELS
// ============================================================================

// ConfidenceLevel is imported from ../types

// ============================================================================
// MASTER DATA TYPES
// ============================================================================

export interface Entity {
  id: string;
  name: string;
  location: string;
  isHeadOffice: boolean;
  employeeCount: number;
}

export interface Customer {
  id: string;
  name: string;
  primaryEntity: string;
  paymentTerms: number; // Days
  paymentReliability: number; // 0-1 (affects confidence)
  averageInvoiceValue: number;
  industry: string;
}

export interface Supplier {
  id: string;
  name: string;
  category: 'Materials' | 'Equipment' | 'Subcontractor' | 'Services' | 'Utilities';
  paymentTerms: number;
  primaryEntity: string;
  averageBillValue: number;
}

export interface Employee {
  id: string;
  name: string;
  entity: string;
  salary: number;
  role: string;
  department: string;
  startDate: Date;
  billableRate?: number; // For time recording (consultants/engineers)
  isActive: boolean;
}

export interface Project {
  id: string;
  name: string;
  customerId: string;
  entity: string;
  startDate: Date;
  endDate: Date;
  totalValue: number;
  status: 'active' | 'completed' | 'on-hold';
  projectType: string;
}

// ============================================================================
// SYSTEM 1: ACCOUNTING SYSTEM TYPES
// ============================================================================

export interface Invoice {
  id: string;
  invoiceNumber: string;
  customerId: string;
  projectId?: string;
  entity: string;
  amount: number;
  issueDate: Date;
  dueDate: Date;
  paidDate?: Date;
  status: 'draft' | 'sent' | 'paid' | 'overdue';
  confidence?: ConfidenceLevel;
  sourceSystem: 'accounting' | 'time-recording' | 'sales-order' | 'crm';
  sourceRecordId?: string;
}

export interface Bill {
  id: string;
  billNumber: string;
  supplierId: string;
  projectId?: string;
  entity: string;
  amount: number;
  billDate: Date;
  dueDate: Date;
  paidDate?: Date;
  status: 'received' | 'approved' | 'paid' | 'overdue';
  category: TransactionCategory;
  sourceSystem: 'accounting' | 'purchase-order';
  sourceRecordId?: string;
}

export interface BankTransaction {
  id: string;
  date: Date;
  description: string;
  amount: number;
  type: 'income' | 'expense';
  category: TransactionCategory;
  entity: string;
  status: string;
  confidence?: ConfidenceLevel;
  accountId: string; // Which bank account
  
  // Linking fields
  sourceSystem: 'accounting' | 'payroll' | 'time-recording' | 'crm' | 'purchase-order' | 'sales-order' | 'budget';
  sourceRecordId?: string;
  invoiceId?: string;
  billId?: string;
  payrollRunId?: string;
}

// ============================================================================
// SYSTEM 2: PAYROLL SYSTEM TYPES
// ============================================================================

export interface PayrollRun {
  id: string;
  runDate: Date;
  payPeriodStart: Date;
  payPeriodEnd: Date;
  entity: string; // 'ALL' for company-wide
  totalGross: number;
  totalNet: number;
  totalTax: number;
  totalNI: number;
  status: 'draft' | 'approved' | 'paid';
  employeePayments: EmployeePayment[];
}

export interface EmployeePayment {
  employeeId: string;
  entity: string;
  grossPay: number;
  netPay: number;
  tax: number;
  ni: number;
}

export interface TaxPayment {
  id: string;
  type: 'PAYE' | 'NIC' | 'VAT';
  dueDate: Date;
  amount: number;
  period: string; // e.g., "2025-12"
  status: 'scheduled' | 'paid';
  entity: string;
}

// ============================================================================
// SYSTEM 3: TIME RECORDING SYSTEM TYPES
// ============================================================================

export interface TimeSheet {
  id: string;
  employeeId: string;
  projectId: string;
  customerId?: string;
  entity: string;
  date: Date;
  hours: number;
  billableRate: number;
  value: number; // hours × rate
  billed: boolean;
  invoiceId?: string;
}

export interface UnbilledTime {
  projectId: string;
  customerId: string;
  entity: string;
  totalHours: number;
  totalValue: number;
  oldestDate: Date;
  expectedInvoiceDate: Date;
  confidence: ConfidenceLevel;
}

// ============================================================================
// SYSTEM 4: PURCHASE & SALES ORDERS TYPES
// ============================================================================

export interface PurchaseOrder {
  id: string;
  poNumber: string;
  supplierId: string;
  projectId?: string;
  entity: string;
  amount: number;
  orderDate: Date;
  expectedDeliveryDate: Date;
  status: 'draft' | 'sent' | 'received' | 'invoiced';
  category: TransactionCategory;
  billId?: string;
}

export interface SalesOrder {
  id: string;
  soNumber: string;
  customerId: string;
  projectId?: string;
  entity: string;
  amount: number;
  orderDate: Date;
  expectedDeliveryDate: Date;
  status: 'draft' | 'confirmed' | 'delivered' | 'invoiced';
  invoiceId?: string;
}

// ============================================================================
// SYSTEM 5: CRM PIPELINE TYPES
// ============================================================================

export interface Opportunity {
  id: string;
  dealName: string;
  customerId: string;
  entity: string;
  value: number;
  probability: number; // 0-1
  stage: 'prospecting' | 'qualification' | 'proposal' | 'negotiation' | 'closing';
  expectedCloseDate: Date;
  confidence: ConfidenceLevel;
  projectId?: string;
  invoiceId?: string;
}

// ============================================================================
// SYSTEM 6: BUDGET SYSTEM TYPES
// ============================================================================

export interface MonthlyBudget {
  id: string;
  month: string; // "2026-03"
  entity: string;
  category: TransactionCategory;
  budgetedIncome: number;
  budgetedExpense: number;
  confidence: 'possible';
}

// ============================================================================
// MASTER DATA GENERATION
// ============================================================================

export const ENTITIES: Entity[] = [
  { id: 'leicester', name: 'Leicester', location: 'Leicester, UK', isHeadOffice: true, employeeCount: 50 },
];

export const CUSTOMERS: Customer[] = [
  { id: 'cust-001', name: 'Leicester City Council', primaryEntity: 'leicester', paymentTerms: 60, paymentReliability: 1.0, averageInvoiceValue: 60000, industry: 'Public Sector' },
  { id: 'cust-002', name: 'University of Leicester', primaryEntity: 'leicester', paymentTerms: 60, paymentReliability: 0.98, averageInvoiceValue: 55000, industry: 'Education' },
  { id: 'cust-003', name: 'Leicester Royal Infirmary', primaryEntity: 'leicester', paymentTerms: 60, paymentReliability: 0.96, averageInvoiceValue: 45000, industry: 'Healthcare' },
  { id: 'cust-004', name: 'Midlands Property Group', primaryEntity: 'leicester', paymentTerms: 30, paymentReliability: 0.95, averageInvoiceValue: 50000, industry: 'Commercial' },
  { id: 'cust-005', name: 'Heritage Homes Leicester', primaryEntity: 'leicester', paymentTerms: 30, paymentReliability: 0.92, averageInvoiceValue: 40000, industry: 'Residential' },
  { id: 'cust-006', name: 'Leicester Shopping Centre Management', primaryEntity: 'leicester', paymentTerms: 45, paymentReliability: 0.88, averageInvoiceValue: 35000, industry: 'Retail' },
  { id: 'cust-007', name: 'DMU (De Montfort University)', primaryEntity: 'leicester', paymentTerms: 60, paymentReliability: 0.97, averageInvoiceValue: 48000, industry: 'Education' },
  { id: 'cust-008', name: 'Fosse Park Developments', primaryEntity: 'leicester', paymentTerms: 30, paymentReliability: 0.90, averageInvoiceValue: 42000, industry: 'Mixed Use' },
];

export const SUPPLIERS: Supplier[] = [
  // Materials suppliers (£24K/month target)
  { id: 'supp-001', name: 'Jewson Leicester', category: 'Materials', paymentTerms: 30, primaryEntity: 'leicester', averageBillValue: 4500 },
  { id: 'supp-002', name: 'Travis Perkins Leicester', category: 'Materials', paymentTerms: 30, primaryEntity: 'leicester', averageBillValue: 3800 },
  { id: 'supp-003', name: 'Leicester Aggregates', category: 'Materials', paymentTerms: 30, primaryEntity: 'leicester', averageBillValue: 3200 },
  { id: 'supp-004', name: 'Midlands Roofing Supplies', category: 'Materials', paymentTerms: 30, primaryEntity: 'leicester', averageBillValue: 2500 },
  
  // Subcontractors (£16K/month target)
  { id: 'supp-005', name: 'Sparks Electrical Ltd', category: 'Subcontractor', paymentTerms: 30, primaryEntity: 'leicester', averageBillValue: 4200 },
  { id: 'supp-006', name: 'Flowtech Plumbing', category: 'Subcontractor', paymentTerms: 30, primaryEntity: 'leicester', averageBillValue: 3600 },
  { id: 'supp-007', name: 'Heights Scaffolding Leicester', category: 'Subcontractor', paymentTerms: 30, primaryEntity: 'leicester', averageBillValue: 2800 },
  { id: 'supp-008', name: 'Precision Joinery Leicester', category: 'Subcontractor', paymentTerms: 30, primaryEntity: 'leicester', averageBillValue: 3200 },
  
  // Equipment hire (£5K/month target)
  { id: 'supp-009', name: 'Midlands Plant Hire', category: 'Equipment', paymentTerms: 30, primaryEntity: 'leicester', averageBillValue: 1800 },
  { id: 'supp-010', name: 'Tool Station Leicester', category: 'Equipment', paymentTerms: 30, primaryEntity: 'leicester', averageBillValue: 1200 },
  
  // Services & Utilities
  { id: 'supp-011', name: 'Leicester Vehicle Leasing', category: 'Services', paymentTerms: 30, primaryEntity: 'leicester', averageBillValue: 800 },
  { id: 'supp-012', name: 'Office Essentials Leicester', category: 'Services', paymentTerms: 30, primaryEntity: 'leicester', averageBillValue: 450 },
];

// ============================================================================
// EMPLOYEE GENERATION (150 employees distributed across entities)
// ============================================================================

const FIRST_NAMES = [
  'James', 'Mary', 'John', 'Patricia', 'Robert', 'Jennifer', 'Michael', 'Linda',
  'William', 'Elizabeth', 'David', 'Barbara', 'Richard', 'Susan', 'Joseph', 'Jessica',
  'Thomas', 'Sarah', 'Charles', 'Karen', 'Christopher', 'Nancy', 'Daniel', 'Lisa',
  'Matthew', 'Betty', 'Anthony', 'Margaret', 'Mark', 'Sandra', 'Donald', 'Ashley',
  'Steven', 'Kimberly', 'Paul', 'Emily', 'Andrew', 'Donna', 'Joshua', 'Michelle',
  'Kenneth', 'Dorothy', 'Kevin', 'Carol', 'Brian', 'Amanda', 'George', 'Melissa',
  'Edward', 'Deborah', 'Ronald', 'Stephanie', 'Timothy', 'Rebecca', 'Jason', 'Sharon',
  'Jeffrey', 'Laura', 'Ryan', 'Cynthia', 'Jacob', 'Kathleen', 'Gary', 'Amy',
  'Nicholas', 'Shirley', 'Eric', 'Angela', 'Jonathan', 'Helen', 'Stephen', 'Anna',
  'Larry', 'Brenda', 'Justin', 'Pamela', 'Scott', 'Nicole', 'Brandon', 'Emma',
  'Benjamin', 'Samantha', 'Samuel', 'Katherine', 'Raymond', 'Christine', 'Gregory', 'Debra',
  'Frank', 'Rachel', 'Alexander', 'Catherine', 'Patrick', 'Carolyn', 'Jack', 'Janet',
  'Dennis', 'Ruth', 'Jerry', 'Maria', 'Tyler', 'Heather', 'Aaron', 'Diane',
];

const LAST_NAMES = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis',
  'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas',
  'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Thompson', 'White', 'Harris',
  'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson', 'Walker', 'Young', 'Allen',
  'King', 'Wright', 'Scott', 'Torres', 'Nguyen', 'Hill', 'Flores', 'Green',
  'Adams', 'Nelson', 'Baker', 'Hall', 'Rivera', 'Campbell', 'Mitchell', 'Carter',
  'Roberts', 'Gomez', 'Phillips', 'Evans', 'Turner', 'Diaz', 'Parker', 'Cruz',
  'Edwards', 'Collins', 'Reyes', 'Stewart', 'Morris', 'Morales', 'Murphy', 'Cook',
];

const ROLES_BY_DEPARTMENT = {
  'Management': ['CEO', 'Managing Director', 'Operations Director', 'Commercial Director'],
  'Finance': ['Finance Director', 'Financial Controller', 'Management Accountant', 'Accounts Payable Clerk', 'Accounts Receivable Clerk', 'Payroll Administrator'],
  'Site Management': ['Construction Manager', 'Site Manager', 'Project Manager', 'Site Supervisor', 'Site Engineer', 'Quantity Surveyor'],
  'Trades': ['Site Foreman', 'Carpenter', 'Bricklayer', 'Electrician', 'Plumber', 'Plasterer', 'Painter', 'General Labourer'],
  'Health & Safety': ['Health & Safety Manager', 'Health & Safety Officer', 'Site Safety Coordinator'],
  'Estimating': ['Chief Estimator', 'Senior Estimator', 'Estimator', 'Assistant Estimator'],
  'Business Development': ['Business Development Director', 'Business Development Manager', 'Bid Manager', 'Tender Coordinator'],
  'Procurement': ['Procurement Manager', 'Buyer', 'Procurement Coordinator'],
  'HR': ['HR Manager', 'HR Advisor', 'Recruitment Specialist'],
  'IT': ['IT Manager', 'Systems Administrator'],
  'Admin': ['Office Manager', 'Executive Assistant', 'Administrative Assistant', 'Receptionist'],
};

function generateEmployees(): Employee[] {
  const employees: Employee[] = [];
  let employeeCounter = 1;
  
  // Helper to generate random date in past 5 years
  const randomStartDate = () => {
    const yearsAgo = Math.floor(Math.random() * 5);
    const monthsAgo = Math.floor(Math.random() * 12);
    const date = new Date(TODAY);
    date.setFullYear(date.getFullYear() - yearsAgo);
    date.setMonth(date.getMonth() - monthsAgo);
    return date;
  };

  // Helper to get salary based on role (construction industry salaries)
  const getSalary = (role: string): number => {
    if (role.includes('Director') || role === 'CEO' || role === 'Managing Director') return 80000 + Math.random() * 60000;
    if (role.includes('Manager') || role.includes('Chief')) return 50000 + Math.random() * 35000;
    if (role.includes('Senior') || role.includes('Surveyor')) return 38000 + Math.random() * 22000;
    if (role.includes('Estimator') || role.includes('Coordinator') || role.includes('Accountant')) return 30000 + Math.random() * 18000;
    if (role.includes('Foreman') || role.includes('Engineer')) return 32000 + Math.random() * 15000;
    if (role.includes('Electrician') || role.includes('Plumber') || role.includes('Carpenter')) return 28000 + Math.random() * 12000;
    if (role.includes('Bricklayer') || role.includes('Plasterer') || role.includes('Painter')) return 26000 + Math.random() * 10000;
    if (role.includes('Labourer')) return 22000 + Math.random() * 6000;
    return 25000 + Math.random() * 10000;
  };

  // Helper to determine if role is billable (site-based roles)
  const isBillableRole = (role: string, department: string): boolean => {
    return department === 'Site Management' || department === 'Trades';
  };

  // Generate employees for each entity
  ENTITIES.forEach(entity => {
    const targetCount = entity.employeeCount;
    
    // Construction company structure (50 employees total - REVISED)
    // Based on typical small construction firm distribution
    const departmentDistribution: { [key: string]: number } = {
      'Management': 2,              // MD, Operations Director
      'Finance': 2,                 // Finance Manager, Accounts Assistant
      'Site Management': 6,         // Project Managers/Site Supervisors
      'Trades': 28,                 // Site workers (largest group - Bricklayers, Carpenters, Laborers)
      'Health & Safety': 1,         // H&S Officer
      'Estimating': 3,              // Estimating team
      'Business Development': 2,    // BD team
      'Procurement': 2,             // Buying team
      'HR': 1,                      // HR Manager
      'IT': 1,                      // IT support
      'Admin': 2,                   // Office admin
    };

    // Generate employees for each department
    Object.entries(departmentDistribution).forEach(([department, count]) => {
      const roles = ROLES_BY_DEPARTMENT[department as keyof typeof ROLES_BY_DEPARTMENT] || ['Employee'];
      
      for (let i = 0; i < count; i++) {
        const firstName = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
        const lastName = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)];
        const role = roles[Math.floor(Math.random() * roles.length)];
        const salary = getSalary(role);
        const billable = isBillableRole(role, department);
        
        employees.push({
          id: `emp-${String(employeeCounter).padStart(3, '0')}`,
          name: `${firstName} ${lastName}`,
          entity: entity.name,
          salary: Math.round(salary),
          role,
          department,
          startDate: randomStartDate(),
          billableRate: billable ? Math.round((salary / 1800) * 1.5) : undefined, // ~1800 billable hours/year, 1.5x markup
          isActive: true,
        });
        
        employeeCounter++;
      }
    });
  });

  return employees;
}

export const EMPLOYEES = generateEmployees();

// ============================================================================
// PROJECT GENERATION (12 active projects - REVISED)
// ============================================================================

const PROJECT_TYPES = [
  'New Build Residential',
  'Commercial Office Development',
  'School Extension',
  'Hospital Refurbishment',
  'Retail Park Construction',
  'Housing Estate Development',
  'University Campus Building',
  'Sports Facility Construction',
  'Mixed-Use Development',
  'Industrial Unit Construction',
  'Care Home Development',
  'Renovation & Restoration',
];

function generateProjects(): Project[] {
  const projects: Project[] = [];
  
  for (let i = 1; i <= 12; i++) {
    const customer = CUSTOMERS[Math.floor(Math.random() * CUSTOMERS.length)];
    const projectType = PROJECT_TYPES[Math.floor(Math.random() * PROJECT_TYPES.length)];
    
    // Projects started 1-12 months ago
    const startDate = new Date(TODAY);
    startDate.setMonth(startDate.getMonth() - Math.floor(Math.random() * 12) - 1);
    
    // Projects end 1-6 months from now
    const endDate = new Date(TODAY);
    endDate.setMonth(endDate.getMonth() + Math.floor(Math.random() * 6) + 1);
    
    const totalValue = customer.averageInvoiceValue * (2 + Math.random() * 4); // 2-6x average invoice
    
    // 80% active, 15% completed, 5% on-hold
    const rand = Math.random();
    const status = rand < 0.8 ? 'active' : rand < 0.95 ? 'completed' : 'on-hold';
    
    projects.push({
      id: `proj-${String(i).padStart(3, '0')}`,
      name: `${customer.name.split(' ')[0]} ${projectType}`,
      customerId: customer.id,
      entity: 'Leicester', // All projects in Leicester
      startDate,
      endDate,
      totalValue: Math.round(totalValue),
      status,
      projectType,
    });
  }
  
  return projects;
}

export const PROJECTS = generateProjects();

// ============================================================================
// BANK ACCOUNTS
// ============================================================================

// ============================================================================
// CREDIT FACILITY — Main Operating Account
// BuildRight has a £150,000 revolving overdraft with HSBC on their main account.
// Currently £42,000 drawn (e.g. used to bridge a delayed client payment in Jan).
// Facility reviewed annually — next review June 2026.
// Secured on company commercial premises in Leicester.
// ============================================================================
const MAIN_ACCOUNT_FACILITY: CreditFacility = {
  id: 'facility-001',
  type: 'overdraft',
  facilityLimit: 150000,
  facilityDrawn: 42000,
  includeInAvailableCash: true,
  facilityReviewDate: new Date('2026-06-30'),
  securedOn: 'Commercial premises, Meridian Business Park, Leicester',
  interestRateSchedule: [
    {
      id: 'rate-001',
      effectiveFrom: new Date('2024-02-01'),
      rate: 8.0,
      note: 'Base rate + 2.5%',
    },
    {
      id: 'rate-002',
      effectiveFrom: new Date('2025-02-01'),
      rate: 7.5,
      note: 'Base rate reduction applied',
    },
  ],
};

// ============================================================================
// RESTRICTED FUNDS — Project Reserve Account
// This account contains two restricted portions:
//   1. CLIENT MONEY (legal): £14,500 deposit held in trust for Thornton Developments
//      pending practical completion of the Grove Park project (due March 2026).
//      BuildRight cannot touch this — Housing Grants Act obligation.
//   2. FIXED TERM DEPOSIT (contractual): £8,234.83 locked in a 90-day notice
//      sub-account with HSBC. Release date April 2026.
// Only the remaining £0 of this account is freely available (all restricted).
// In practice the £22,734.83 balance is split entirely across the two restrictions.
// ============================================================================
const PROJECT_RESERVE_RESTRICTIONS: RestrictedFund[] = [
  {
    id: 'restrict-001',
    restrictionType: 'client_money',
    label: 'Thornton Developments — Grove Park client deposit',
    restrictedAmount: 14500,
    releaseDate: new Date('2026-03-31'),
  },
  {
    id: 'restrict-002',
    restrictionType: 'fixed_term',
    label: 'HSBC Fixed Term Deposit — Q2 2026',
    restrictedAmount: 8234.83,
    releaseDate: new Date('2026-06-30'),
  },
];

export const BANK_ACCOUNTS: BankAccount[] = [
  {
    id: 'bank-001',
    name: 'Main Operating Account',
    bank: 'HSBC',
    balance: 179432.17,
    currency: 'GBP',
    type: 'Current',
    dataSourceName: 'Access Financials',
    creditFacilities: [MAIN_ACCOUNT_FACILITY],
  },
  {
    id: 'bank-002',
    name: 'Payroll Account',
    bank: 'Barclays',
    balance: 44871.50,
    currency: 'GBP',
    type: 'Current',
    dataSourceName: 'Paycircle',
    // No facility or restrictions — straightforward payroll clearing account
  },
  {
    id: 'bank-003',
    name: 'Project Reserve Account',
    bank: 'HSBC',
    balance: 22734.83,
    currency: 'GBP',
    type: 'Savings',
    dataSourceName: 'Access Financials',
    restrictedFunds: PROJECT_RESERVE_RESTRICTIONS,
  },
];

// Total ledger balance: £247,038.50 (matches TARGET_CASH_POSITION)
// Total restricted:     £22,734.83 (client money + notice account — excluded from available cash)
// Total facility limit: £150,000   (HSBC overdraft on main account)
// Total facility drawn: £42,000    (currently utilised)
// Facility headroom:    £108,000   (undrawn — available if needed)
// ─────────────────────────────────────────────────────────────────
// AVAILABLE CASH:       £224,303.67 (ledger balance − restricted)
// MAX AVAILABLE CASH:   £332,303.67 (available cash + facility headroom)

// ============================================================================
// MANUALLY DECLARED FINANCING FACILITIES (Zone 2)
// These are financing arrangements that exist outside connected data sources.
// BuildRight has an invoice discounting facility with Aldermore that is not
// directly connected — it is declared manually here.
// ============================================================================
export const MANUAL_FINANCING_FACILITIES: ManualFinancingFacility[] = [
  {
    id: 'manual-facility-001',
    name: 'Aldermore Invoice Discounting Facility',
    notes: 'Confidential invoice discounting — customers are unaware. Used selectively for large project invoices over £25,000.',
    facility: {
      id: 'manual-facility-001-detail',
      type: 'invoice_finance',
      facilityLimit: 400000,
      facilityDrawn: 87500,
      includeInAvailableCash: true,
      invoiceFinanceType: 'discounting',
      advanceRatePercent: 85,
      concentrationLimitPercent: 25,
      discountChargePercent: 2.8,
      serviceFeePercent: 0.3,
      fundingLineCeiling: 400000,
      eligibleDebtorCriteria: 'UK debtors only, max invoice age 90 days, no disputed invoices',
      interestRateSchedule: [
        {
          id: 'inv-rate-001',
          effectiveFrom: new Date('2024-06-01'),
          rate: 2.8,
          note: 'Base rate + 0.3%',
        },
      ],
    },
  },
];

// ============================================================================
// MASTER DATA SUMMARY
// ============================================================================

console.log('✅ MASTER DATA GENERATED:');
console.log(`   - ${ENTITIES.length} Entities (Total employees: ${ENTITIES.reduce((sum, e) => sum + e.employeeCount, 0)})`);
console.log(`   - ${EMPLOYEES.length} Employees`);
console.log(`   - ${CUSTOMERS.length} Customers`);
console.log(`   - ${SUPPLIERS.length} Suppliers`);
console.log(`   - ${PROJECTS.length} Projects`);
console.log(`   - ${BANK_ACCOUNTS.length} Bank Accounts (Total: £${BANK_ACCOUNTS.reduce((sum, b) => sum + b.balance, 0).toLocaleString()})`);

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

function randomBetween(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randomChoice<T>(array: T[]): T {
  return array[Math.floor(Math.random() * array.length)];
}

// ============================================================================
// SYSTEM 1: ACCOUNTING SYSTEM
// ============================================================================

/**
 * ACCOUNTING SYSTEM GENERATION STRATEGY (REVISED - Section 2):
 * 
 * Based on BuildRight Construction Ltd profile (50 employees, £5M turnover):
 * - Monthly expenses: £63,333
 * - Monthly revenue: £416,667
 * - Current date: February 5, 2026
 * 
 * 1. Generate 90 days of PAID bills (Nov 7, 2025 → Feb 5, 2026) totaling ~£190K
 * 2. Generate 25 OUTSTANDING invoices (5 overdue, 10 due Feb, 10 future) totaling ~£1.25M
 * 3. Generate 30 OUTSTANDING bills (4 overdue, 18 due Feb, 8 future) totaling ~£190K
 * 4. Generate bank transactions for all PAID items
 * 5. Generate payroll runs (biweekly)
 * 6. Link everything with referential integrity
 */

interface AccountingData {
  invoices: Invoice[];
  bills: Bill[];
  bankTransactions: BankTransaction[];
  payrollRuns: PayrollRun[];
}

function generateAccountingSystem(): AccountingData {
  const invoices: Invoice[] = [];
  const bills: Bill[] = [];
  const bankTransactions: BankTransaction[] = [];
  const payrollRuns: PayrollRun[] = [];
  
  let invoiceCounter = 1;
  let billCounter = 1;
  let bankTxnCounter = 1;
  let payrollCounter = 1;

  // ============================================================================
  // STEP 1: Generate HISTORICAL PAID INVOICES (Past 90 days for revenue history)
  // ============================================================================
  
  console.log('🚀🚀🚀 SECTION 2 DATA GENERATION STARTING 🚀🚀🚀');
  console.log('Generating historical paid invoices (past 90 days)...');
  
  // Generate ~15 paid invoices over past 90 days (avg 5 per month)
  // Total should be ~£1.25M (3 months × £416K)
  const historicalInvoiceCount = 15;
  const targetHistoricalRevenue = MONTHLY_REVENUE * 3; // £1.25M
  const avgHistoricalInvoice = targetHistoricalRevenue / historicalInvoiceCount; // ~£83K
  
  for (let i = 0; i < historicalInvoiceCount; i++) {
    const customer = randomChoice(CUSTOMERS);
    const project = randomChoice(PROJECTS.filter(p => p.customerId === customer.id));
    
    // Random date in past 90 days (Nov 7, 2025 → Feb 5, 2026)
    const daysAgo = randomBetween(1, 90);
    const issueDate = addDays(TODAY, -daysAgo - customer.paymentTerms);
    const dueDate = addDays(issueDate, customer.paymentTerms);
    
    // Payment date: based on customer reliability
    const paymentDelay = customer.paymentReliability > 0.9 
      ? randomBetween(-2, 5) // Good payers: early to slightly late
      : randomBetween(0, 15); // Poor payers: on time to quite late
    const paidDate = addDays(dueDate, paymentDelay);
    
    // Invoice amount: vary around average (£35K-£120K)
    const amount = Math.round((avgHistoricalInvoice * (0.4 + Math.random() * 1.0)) / 1000) * 1000; // Round to nearest £1K
    
    const invoice: Invoice = {
      id: `inv-${String(invoiceCounter).padStart(4, '0')}`,
      invoiceNumber: `INV-${issueDate.getFullYear()}-${String(invoiceCounter).padStart(4, '0')}`,
      customerId: customer.id,
      projectId: project?.id,
      entity: 'Leicester',
      amount,
      issueDate,
      dueDate,
      paidDate,
      status: 'paid',
      sourceSystem: 'accounting',
      sourceRecordId: `inv-${String(invoiceCounter).padStart(4, '0')}`,
    };
    
    invoices.push(invoice);
    
    // Create corresponding bank transaction (income)
    const bankTxn: BankTransaction = {
      id: `btxn-${String(bankTxnCounter).padStart(5, '0')}`,
      date: paidDate,
      description: `Payment received - ${customer.name} - ${invoice.invoiceNumber}`,
      amount,
      type: 'income',
      category: 'Revenue',
      entity: 'Leicester',
      status: 'Settled',
      accountId: 'bank-001', // Main operating account
      sourceSystem: 'accounting',
      sourceRecordId: invoice.id,
      invoiceId: invoice.id,
    };
    
    bankTransactions.push(bankTxn);
    
    invoiceCounter++;
    bankTxnCounter++;
  }

  // ============================================================================
  // STEP 2: Generate OUTSTANDING INVOICES (25 total = £1.25M)
  // ============================================================================
  
  console.log('Generating outstanding invoices...');
  
  // 16 OVERDUE invoices (£498,929.50 total) - Fixed realistic amounts matching useDrillDown DEMO_OVERDUE_INVOICES
  // Customer names must match CUSTOMERS array entries exactly
  const overdueInvoices = [
    { customer: 'Leicester City Council', amount: 74432.50, daysOverdue: 103, invoiceNum: 2047 },
    { customer: 'Heritage Homes Leicester', amount: 56750.00, daysOverdue: 31, invoiceNum: 2051 },
    { customer: 'Fosse Park Developments', amount: 62200.00, daysOverdue: 38, invoiceNum: 2049 },
    { customer: 'Leicester Royal Infirmary', amount: 45340.00, daysOverdue: 56, invoiceNum: 2044 },
    { customer: 'Midlands Property Group', amount: 39825.75, daysOverdue: 22, invoiceNum: 2053 },
    { customer: 'Leicester City Council', amount: 41200.00, daysOverdue: 67, invoiceNum: 2041 },
    { customer: 'University of Leicester', amount: 38475.50, daysOverdue: 14, invoiceNum: 2055 },
    { customer: 'Heritage Homes Leicester', amount: 29340.00, daysOverdue: 71, invoiceNum: 2039 },
    { customer: 'DMU (De Montfort University)', amount: 24650.25, daysOverdue: 8, invoiceNum: 2057 },
    { customer: 'Fosse Park Developments', amount: 19387.50, daysOverdue: 84, invoiceNum: 2036 },
    { customer: 'Midlands Property Group', amount: 16840.00, daysOverdue: 5, invoiceNum: 2059 },
    { customer: 'Leicester Royal Infirmary', amount: 14275.75, daysOverdue: 91, invoiceNum: 2033 },
    { customer: 'University of Leicester', amount: 12920.50, daysOverdue: 3, invoiceNum: 2061 },
    { customer: 'DMU (De Montfort University)', amount: 9450.00, daysOverdue: 98, invoiceNum: 2031 },
    { customer: 'Leicester Shopping Centre Management', amount: 7837.50, daysOverdue: 2, invoiceNum: 2063 },
    { customer: 'Leicester Shopping Centre Management', amount: 6204.75, daysOverdue: 103, invoiceNum: 2029 },
  ];
  
  for (const inv of overdueInvoices) {
    const customer = CUSTOMERS.find(c => c.name === inv.customer) || CUSTOMERS[0];
    const project = randomChoice(PROJECTS.filter(p => p.customerId === customer.id));
    
    const dueDate = addDays(TODAY, -inv.daysOverdue);
    const issueDate = addDays(dueDate, -customer.paymentTerms);
    
    const invoice: Invoice = {
      id: `inv-${String(inv.invoiceNum).padStart(4, '0')}`,
      invoiceNumber: `INV-${issueDate.getFullYear()}-${String(inv.invoiceNum).padStart(4, '0')}`,
      customerId: customer.id,
      projectId: project?.id,
      entity: 'Leicester',
      amount: inv.amount,
      issueDate,
      dueDate,
      status: 'overdue',
      confidence: 'probable',
      sourceSystem: 'accounting',
      sourceRecordId: `inv-${String(inv.invoiceNum).padStart(4, '0')}`,
    };
    
    invoices.push(invoice);
    invoiceCounter = Math.max(invoiceCounter, inv.invoiceNum + 1);
  }
  
  // 10 DUE IN CURRENT MONTH (£450,000 total)
  // Range: £25K-£95K per invoice
  const currentMonthInvoiceAmounts = [95000, 68000, 52000, 48000, 42000, 38000, 35000, 32000, 28000, 25000];
  
  for (let i = 0; i < 10; i++) {
    const customer = randomChoice(CUSTOMERS);
    const project = randomChoice(PROJECTS.filter(p => p.customerId === customer.id));
    
    // Due date in current month (spread across remaining days of month)
    const daysInMonth = END_OF_CURRENT_MONTH.getDate();
    const currentDay = TODAY.getDate();
    const remainingDays = daysInMonth - currentDay;
    
    // If we're near end of month, spread across whole month, otherwise spread from today onwards
    const minDay = remainingDays < 10 ? 1 : currentDay + 1;
    const maxDay = Math.min(daysInMonth, 28); // Avoid month-end edge cases
    const dueDay = randomBetween(minDay, maxDay);
    const dueDate = new Date(TODAY.getFullYear(), TODAY.getMonth(), dueDay);
    const issueDate = addDays(dueDate, -customer.paymentTerms);
    
    const invoice: Invoice = {
      id: `inv-${String(invoiceCounter).padStart(4, '0')}`,
      invoiceNumber: `INV-${TODAY.getFullYear()}-${String(invoiceCounter).padStart(4, '0')}`,
      customerId: customer.id,
      projectId: project?.id,
      entity: 'Leicester',
      amount: currentMonthInvoiceAmounts[i],
      issueDate,
      dueDate,
      status: 'sent',
      confidence: customer.paymentReliability > 0.9 ? 'certain' : 'probable',
      sourceSystem: 'accounting',
      sourceRecordId: `inv-${String(invoiceCounter).padStart(4, '0')}`,
    };
    
    invoices.push(invoice);
    invoiceCounter++;
  }
  
  // 10 FUTURE INVOICES (next 1-2 months, £620,000 total)
  // Range: £35K-£120K per invoice
  const futureInvoiceAmounts = [120000, 95000, 78000, 68000, 58000, 52000, 48000, 42000, 38000, 35000];
  
  for (let i = 0; i < 10; i++) {
    const customer = randomChoice(CUSTOMERS);
    const project = randomChoice(PROJECTS.filter(p => p.customerId === customer.id));
    
    // Due date in next 1-2 months (26-80 days ahead)
    const daysAhead = randomBetween(26, 80);
    const dueDate = new Date(TODAY.getTime() + (daysAhead * 24 * 60 * 60 * 1000));
    const issueDate = addDays(dueDate, -customer.paymentTerms);
    
    // Confidence decreases with time
    let confidence: ConfidenceLevel = 'certain';
    const monthsAhead = Math.floor(daysAhead / 30);
    if (monthsAhead > 1 || customer.paymentReliability < 0.85) {
      confidence = 'probable';
    }
    
    const invoice: Invoice = {
      id: `inv-${String(invoiceCounter).padStart(4, '0')}`,
      invoiceNumber: `INV-${TODAY.getFullYear()}-${String(invoiceCounter).padStart(4, '0')}`,
      customerId: customer.id,
      projectId: project?.id,
      entity: 'Leicester',
      amount: futureInvoiceAmounts[i],
      issueDate,
      dueDate,
      status: 'sent',
      confidence,
      sourceSystem: 'accounting',
      sourceRecordId: `inv-${String(invoiceCounter).padStart(4, '0')}`,
    };
    
    invoices.push(invoice);
    invoiceCounter++;
  }

  // ============================================================================
  // STEP 3: Generate HISTORICAL PAID BILLS (Past 90 days = £190K)
  // ============================================================================
  
  console.log('Generating historical paid bills (past 90 days)...');
  
  // Generate bills for past 90 days totaling ~£190K (3 months × £63,333)
  // Materials: ~£24K/month = ~75 transactions over 90 days
  // Subcontractors: ~£16K/month = ~24 transactions over 90 days
  // Equipment: ~£5K/month = ~18 transactions over 90 days
  // Operating expenses: ~£18K/month = ~90 transactions over 90 days
  // Total: ~207 transactions over 90 days
  
  // MATERIALS (£72K over 90 days = ~75 transactions)
  const materialSuppliers = SUPPLIERS.filter(s => s.category === 'Materials');
  const materialsTransactions = [
    { supplier: 'Jewson Leicester', count: 27, avgAmount: 1200 }, // 8-10/month × 3 months = £32.4K
    { supplier: 'Travis Perkins Leicester', count: 21, avgAmount: 700 }, // 6-8/month × 3 months = £14.7K
    { supplier: 'Leicester Aggregates', count: 12, avgAmount: 1500 }, // 3-5/month × 3 months = £18K
    { supplier: 'Midlands Roofing Supplies', count: 8, avgAmount: 900 }, // 2-3/month × 3 months = £7.2K
  ]; // Total: ~£72K
  
  for (const mt of materialsTransactions) {
    const supplier = SUPPLIERS.find(s => s.name === mt.supplier) || materialSuppliers[0];
    for (let i = 0; i < mt.count; i++) {
      const daysAgo = randomBetween(1, 90);
      const billDate = addDays(TODAY, -daysAgo);
      const dueDate = addDays(billDate, supplier.paymentTerms);
      const paymentDelay = randomBetween(-1, 3);
      const paidDate = addDays(dueDate, paymentDelay);
      const amount = Math.round((mt.avgAmount * (0.6 + Math.random() * 0.8)) / 50) * 50;
      
      const bill: Bill = {
        id: `bill-${String(billCounter).padStart(4, '0')}`,
        billNumber: `BILL-${billDate.getFullYear()}-${String(billCounter).padStart(4, '0')}`,
        supplierId: supplier.id,
        entity: 'Leicester',
        amount,
        billDate,
        dueDate,
        paidDate,
        status: 'paid',
        category: 'Materials',
        sourceSystem: 'accounting',
        sourceRecordId: `bill-${String(billCounter).padStart(4, '0')}`,
      };
      bills.push(bill);
      
      const bankTxn: BankTransaction = {
        id: `btxn-${String(bankTxnCounter).padStart(5, '0')}`,
        date: paidDate,
        description: `Payment to ${supplier.name} - ${bill.billNumber}`,
        amount: -amount,
        type: 'expense',
        category: 'Materials',
        entity: 'Leicester',
        status: 'Settled',
        accountId: 'bank-001',
        sourceSystem: 'accounting',
        sourceRecordId: bill.id,
        billId: bill.id,
      };
      bankTransactions.push(bankTxn);
      
      billCounter++;
      bankTxnCounter++;
    }
  }
  
  // SUBCONTRACTORS (£48K over 90 days = ~24 transactions)
  const subcontractorTransactions = [
    { supplier: 'Sparks Electrical Ltd', count: 7, avgAmount: 2100 }, // 2-3/month × 3 months = £14.7K
    { supplier: 'Flowtech Plumbing', count: 7, avgAmount: 1750 }, // 2-3/month × 3 months = £12.25K
    { supplier: 'Heights Scaffolding Leicester', count: 5, avgAmount: 2600 }, // 1-2/month × 3 months = £13K
    { supplier: 'Precision Joinery Leicester', count: 5, avgAmount: 1600 }, // 1-2/month × 3 months = £8K
  ]; // Total: ~£48K
  
  for (const st of subcontractorTransactions) {
    const supplier = SUPPLIERS.find(s => s.name === st.supplier) || SUPPLIERS.find(s => s.category === 'Subcontractor')!;
    for (let i = 0; i < st.count; i++) {
      const daysAgo = randomBetween(1, 90);
      const billDate = addDays(TODAY, -daysAgo);
      const dueDate = addDays(billDate, supplier.paymentTerms);
      const paymentDelay = randomBetween(-1, 3);
      const paidDate = addDays(dueDate, paymentDelay);
      const amount = Math.round((st.avgAmount * (0.7 + Math.random() * 0.6)) / 100) * 100;
      
      const bill: Bill = {
        id: `bill-${String(billCounter).padStart(4, '0')}`,
        billNumber: `BILL-${billDate.getFullYear()}-${String(billCounter).padStart(4, '0')}`,
        supplierId: supplier.id,
        entity: 'Leicester',
        amount,
        billDate,
        dueDate,
        paidDate,
        status: 'paid',
        category: 'Subcontractors',
        sourceSystem: 'accounting',
        sourceRecordId: `bill-${String(billCounter).padStart(4, '0')}`,
      };
      bills.push(bill);
      
      const bankTxn: BankTransaction = {
        id: `btxn-${String(bankTxnCounter).padStart(5, '0')}`,
        date: paidDate,
        description: `Payment to ${supplier.name} - ${bill.billNumber}`,
        amount: -amount,
        type: 'expense',
        category: 'Subcontractors',
        entity: 'Leicester',
        status: 'Settled',
        accountId: 'bank-001',
        sourceSystem: 'accounting',
        sourceRecordId: bill.id,
        billId: bill.id,
      };
      bankTransactions.push(bankTxn);
      
      billCounter++;
      bankTxnCounter++;
    }
  }
  
  // EQUIPMENT HIRE (£15K over 90 days = ~18 transactions)
  const equipmentTransactions = [
    { supplier: 'Midlands Plant Hire', count: 8, avgAmount: 1100 }, // 2-3/month × 3 months = £8.8K
    { supplier: 'Tool Station Leicester', count: 10, avgAmount: 620 }, // 3-4/month × 3 months = £6.2K
  ]; // Total: ~£15K
  
  for (const et of equipmentTransactions) {
    const supplier = SUPPLIERS.find(s => s.name === et.supplier) || SUPPLIERS.find(s => s.category === 'Equipment')!;
    for (let i = 0; i < et.count; i++) {
      const daysAgo = randomBetween(1, 90);
      const billDate = addDays(TODAY, -daysAgo);
      const dueDate = addDays(billDate, supplier.paymentTerms);
      const paymentDelay = randomBetween(-1, 3);
      const paidDate = addDays(dueDate, paymentDelay);
      const amount = Math.round((et.avgAmount * (0.6 + Math.random() * 0.8)) / 50) * 50;
      
      const bill: Bill = {
        id: `bill-${String(billCounter).padStart(4, '0')}`,
        billNumber: `BILL-${billDate.getFullYear()}-${String(billCounter).padStart(4, '0')}`,
        supplierId: supplier.id,
        entity: 'Leicester',
        amount,
        billDate,
        dueDate,
        paidDate,
        status: 'paid',
        category: 'Equipment',
        sourceSystem: 'accounting',
        sourceRecordId: `bill-${String(billCounter).padStart(4, '0')}`,
      };
      bills.push(bill);
      
      const bankTxn: BankTransaction = {
        id: `btxn-${String(bankTxnCounter).padStart(5, '0')}`,
        date: paidDate,
        description: `Payment to ${supplier.name} - ${bill.billNumber}`,
        amount: -amount,
        type: 'expense',
        category: 'Equipment',
        entity: 'Leicester',
        status: 'Settled',
        accountId: 'bank-001',
        sourceSystem: 'accounting',
        sourceRecordId: bill.id,
        billId: bill.id,
      };
      bankTransactions.push(bankTxn);
      
      billCounter++;
      bankTxnCounter++;
    }
  }
  
  // OPERATING EXPENSES (£15.3K over 90 days = ~108 small transactions)
  // Office rent, utilities, insurance, vehicle costs, software, professional fees, marketing, sundries
  const operatingExpenses = [
    { description: 'Office Rent', count: 3, amount: 1200, category: 'Office Expenses' }, // £3.6K
    { description: 'Electric', count: 3, amount: 250, category: 'Utilities' }, // £750
    { description: 'Gas', count: 3, amount: 150, category: 'Utilities' }, // £450
    { description: 'Business Insurance', count: 3, amount: 217, category: 'Professional Services' }, // £651
    { description: 'Vehicle Fuel', count: 24, amount: 100, category: 'Vehicle Expenses' }, // £2.4K
    { description: 'Software Licenses', count: 3, amount: 450, category: 'Office Expenses' }, // £1.35K
    { description: 'Professional Fees', count: 9, amount: 167, category: 'Professional Services' }, // £1.5K
    { description: 'Marketing', count: 9, amount: 100, category: 'Marketing' }, // £900
    { description: 'Office Supplies', count: 15, amount: 40, category: 'Office Expenses' }, // £600
    { description: 'Sundries', count: 36, amount: 67, category: 'Office Expenses' }, // £2.4K
  ]; // Total: ~£15.3K
  
  for (const oe of operatingExpenses) {
    for (let i = 0; i < oe.count; i++) {
      const daysAgo = randomBetween(1, 90);
      const billDate = addDays(TODAY, -daysAgo);
      const dueDate = addDays(billDate, 30);
      const paymentDelay = randomBetween(-1, 2);
      const paidDate = addDays(dueDate, paymentDelay);
      const amount = Math.round(oe.amount * (0.85 + Math.random() * 0.3) * 100) / 100;
      
      const supplier = oe.description === 'Office Rent' ? SUPPLIERS.find(s => s.name.includes('Office')) || SUPPLIERS[0] :
                       oe.description === 'Electric' || oe.description === 'Gas' ? SUPPLIERS.find(s => s.category === 'Utilities') || SUPPLIERS[0] :
                       SUPPLIERS.find(s => s.category === 'Services') || SUPPLIERS[0];
      
      const bill: Bill = {
        id: `bill-${String(billCounter).padStart(4, '0')}`,
        billNumber: `BILL-${billDate.getFullYear()}-${String(billCounter).padStart(4, '0')}`,
        supplierId: supplier.id,
        entity: 'Leicester',
        amount,
        billDate,
        dueDate,
        paidDate,
        status: 'paid',
        category: oe.category as TransactionCategory,
        sourceSystem: 'accounting',
        sourceRecordId: `bill-${String(billCounter).padStart(4, '0')}`,
      };
      bills.push(bill);
      
      const bankTxn: BankTransaction = {
        id: `btxn-${String(bankTxnCounter).padStart(5, '0')}`,
        date: paidDate,
        description: `${oe.description} - ${bill.billNumber}`,
        amount: -amount,
        type: 'expense',
        category: oe.category as TransactionCategory,
        entity: 'Leicester',
        status: 'Settled',
        accountId: 'bank-001',
        sourceSystem: 'accounting',
        sourceRecordId: bill.id,
        billId: bill.id,
      };
      bankTransactions.push(bankTxn);
      
      billCounter++;
      bankTxnCounter++;
    }
  }

  // ============================================================================
  // STEP 4: Generate OUTSTANDING BILLS (30 total = £190K)
  // ============================================================================
  
  console.log('Generating outstanding bills...');
  
  // 4 OVERDUE bills - realistic messy amounts
  const overdueBills = [
    { supplier: 'Jewson Leicester', amount: 8247.50, daysOverdue: 14, category: 'Materials' },
    { supplier: 'Travis Perkins Leicester', amount: 5083.75, daysOverdue: 10, category: 'Materials' },
    { supplier: 'Sparks Electrical Ltd', amount: 3762.00, daysOverdue: 6, category: 'Subcontractors' },
    { supplier: 'Midlands Plant Hire', amount: 1435.25, daysOverdue: 8, category: 'Equipment' },
  ];
  
  for (const ob of overdueBills) {
    const supplier = SUPPLIERS.find(s => s.name === ob.supplier) || SUPPLIERS[0];
    const dueDate = addDays(TODAY, -ob.daysOverdue);
    const billDate = addDays(dueDate, -supplier.paymentTerms);
    
    const bill: Bill = {
      id: `bill-${String(billCounter).padStart(4, '0')}`,
      billNumber: `BILL-${billDate.getFullYear()}-${String(billCounter).padStart(4, '0')}`,
      supplierId: supplier.id,
      entity: 'Leicester',
      amount: ob.amount,
      billDate,
      dueDate,
      status: 'overdue',
      category: ob.category as TransactionCategory,
      sourceSystem: 'accounting',
      sourceRecordId: `bill-${String(billCounter).padStart(4, '0')}`,
    };
    
    bills.push(bill);
    billCounter++;
  }
  
  // 18 DUE IN CURRENT MONTH (£115,000 total)
  // Materials: 8 bills, £55K total
  // Subcontractors: 6 bills, £42K total
  // Equipment: 2 bills, £6K total
  // Operating: 2 bills, £12K total
  
  const currentMonthBills = [
    // Materials (8 bills)
    { supplier: 'Jewson Leicester', amount: 8473.25, category: 'Materials' },
    { supplier: 'Travis Perkins Leicester', amount: 7184.50, category: 'Materials' },
    { supplier: 'Leicester Aggregates', amount: 9847.75, category: 'Materials' },
    { supplier: 'Jewson Leicester', amount: 6521.00, category: 'Materials' },
    { supplier: 'Midlands Roofing Supplies', amount: 7832.50, category: 'Materials' },
    { supplier: 'Travis Perkins Leicester', amount: 5267.25, category: 'Materials' },
    { supplier: 'Leicester Aggregates', amount: 6043.75, category: 'Materials' },
    { supplier: 'Jewson Leicester', amount: 4018.50, category: 'Materials' },
    // Subcontractors (6 bills)
    { supplier: 'Sparks Electrical Ltd', amount: 8534.00, category: 'Subcontractors' },
    { supplier: 'Flowtech Plumbing', amount: 7247.50, category: 'Subcontractors' },
    { supplier: 'Heights Scaffolding Leicester', amount: 9823.75, category: 'Subcontractors' },
    { supplier: 'Precision Joinery Leicester', amount: 6478.25, category: 'Subcontractors' },
    { supplier: 'Sparks Electrical Ltd', amount: 5512.50, category: 'Subcontractors' },
    { supplier: 'Flowtech Plumbing', amount: 4487.75, category: 'Subcontractors' },
    // Equipment (2 bills)
    { supplier: 'Midlands Plant Hire', amount: 3524.50, category: 'Equipment' },
    { supplier: 'Tool Station Leicester', amount: 2487.25, category: 'Equipment' },
    // Operating (2 bills)
    { supplier: 'Office Essentials Leicester', amount: 6973.50, category: 'Office Expenses' },
    { supplier: 'Leicester Vehicle Leasing', amount: 4987.25, category: 'Vehicle Expenses' },
  ];
  
  for (let i = 0; i < currentMonthBills.length; i++) {
    const fb = currentMonthBills[i];
    const supplier = SUPPLIERS.find(s => s.name === fb.supplier) || SUPPLIERS[0];
    
    // Due date in current month (spread across remaining days of month)
    const daysInMonth = END_OF_CURRENT_MONTH.getDate();
    const currentDay = TODAY.getDate();
    const remainingDays = daysInMonth - currentDay;
    const minDay = remainingDays < 10 ? 1 : currentDay + 1;
    const maxDay = Math.min(daysInMonth, 28);
    const dueDay = randomBetween(minDay, maxDay);
    const dueDate = new Date(TODAY.getFullYear(), TODAY.getMonth(), dueDay);
    const billDate = addDays(dueDate, -supplier.paymentTerms);
    
    const bill: Bill = {
      id: `bill-${String(billCounter).padStart(4, '0')}`,
      billNumber: `BILL-${TODAY.getFullYear()}-${String(billCounter).padStart(4, '0')}`,
      supplierId: supplier.id,
      entity: 'Leicester',
      amount: fb.amount,
      billDate,
      dueDate,
      status: 'received',
      category: fb.category as TransactionCategory,
      sourceSystem: 'accounting',
      sourceRecordId: `bill-${String(billCounter).padStart(4, '0')}`,
    };
    
    bills.push(bill);
    billCounter++;
  }
  
  // 8 FUTURE BILLS (next month, £56,500 total)
  const futureBills = [
    { supplier: 'Jewson Leicester', amount: 9523.75, category: 'Materials' },
    { supplier: 'Leicester Aggregates', amount: 8247.50, category: 'Materials' },
    { supplier: 'Sparks Electrical Ltd', amount: 10834.25, category: 'Subcontractors' },
    { supplier: 'Heights Scaffolding Leicester', amount: 9012.50, category: 'Subcontractors' },
    { supplier: 'Flowtech Plumbing', amount: 7487.75, category: 'Subcontractors' },
    { supplier: 'Midlands Plant Hire', amount: 4523.25, category: 'Equipment' },
    { supplier: 'Travis Perkins Leicester', amount: 4037.50, category: 'Materials' },
    { supplier: 'Precision Joinery Leicester', amount: 3018.75, category: 'Subcontractors' },
  ];
  
  for (let i = 0; i < futureBills.length; i++) {
    const fb = futureBills[i];
    const supplier = SUPPLIERS.find(s => s.name === fb.supplier) || SUPPLIERS[0];
    
    // Due date in next month (30-60 days ahead)
    const daysAhead = randomBetween(30, 60);
    const dueDate = new Date(TODAY.getTime() + (daysAhead * 24 * 60 * 60 * 1000));
    const billDate = addDays(dueDate, -supplier.paymentTerms);
    
    const bill: Bill = {
      id: `bill-${String(billCounter).padStart(4, '0')}`,
      billNumber: `BILL-${dueDate.getFullYear()}-${String(billCounter).padStart(4, '0')}`,
      supplierId: supplier.id,
      entity: 'Leicester',
      amount: fb.amount,
      billDate,
      dueDate,
      status: 'received',
      category: fb.category as TransactionCategory,
      sourceSystem: 'accounting',
      sourceRecordId: `bill-${String(billCounter).padStart(4, '0')}`,
    };
    
    bills.push(bill);
    billCounter++;
  }

  // ============================================================================
  // STEP 5: Generate PAYROLL RUNS (Biweekly, past 90 days)
  // ============================================================================
  
  console.log('Generating payroll runs...');
  
  // Payroll is biweekly (every 2 weeks) on Fridays
  // Base payroll: £20,000 per run
  // Employer NI: £2,400/month = £1,200 per run (biweekly)
  // Pension: £1,100/month = £550 per run (biweekly)
  // Total per run: £21,750
  
  // Generate payroll runs for past 90 days (approximately 6 runs)
  const payrollDates = [
    addDays(TODAY, -84), // ~12 weeks ago
    addDays(TODAY, -70), // ~10 weeks ago
    addDays(TODAY, -56), // ~8 weeks ago
    addDays(TODAY, -42), // ~6 weeks ago
    addDays(TODAY, -28), // ~4 weeks ago
    addDays(TODAY, -14), // ~2 weeks ago
  ];
  
  // Realistic payroll variations per run — overtime, adjustments, starters/leavers
  // Base gross: £20,000. Each run varies by ±£200–£800 to reflect real-world payroll
  const payrollVariations = [
    { grossAdj: 347.50, niAdj: 41.70, netAdj: 278.00 },  // Run 1: overtime on Kirkby project
    { grossAdj: -124.25, niAdj: -14.91, netAdj: -99.40 }, // Run 2: leaver mid-period adjustment
    { grossAdj: 612.75, niAdj: 73.53, netAdj: 490.20 },  // Run 3: bank holiday overtime
    { grossAdj: 89.50, niAdj: 10.74, netAdj: 71.60 },    // Run 4: minor overtime
    { grossAdj: -287.00, niAdj: -34.44, netAdj: -229.60 }, // Run 5: new starter pro-rata
    { grossAdj: 431.25, niAdj: 51.75, netAdj: 345.00 },  // Run 6: site completion bonus
  ];

  for (let i = 0; i < payrollDates.length; i++) {
    const payrollDate = payrollDates[i];
    const variation = payrollVariations[i] ?? { grossAdj: 0, niAdj: 0, netAdj: 0 };
    const payPeriodEnd = payrollDate;
    const payPeriodStart = addDays(payrollDate, -14);

    const totalGross = Math.round((20000 + variation.grossAdj) * 100) / 100;
    const totalNet = Math.round((16000 + variation.netAdj) * 100) / 100;
    const totalTax = Math.round((totalGross - totalNet - (1200 + variation.niAdj)) * 100) / 100;
    const totalNI = Math.round((1200 + variation.niAdj) * 100) / 100;
    const pensionAmount = Math.round((550 + variation.grossAdj * 0.03) * 100) / 100; // ~3% of gross adj

    // Create PayrollRun object
    const payrollRun: PayrollRun = {
      id: `payroll-${String(payrollCounter).padStart(4, '0')}`,
      runDate: payrollDate,
      payPeriodStart,
      payPeriodEnd,
      entity: 'Leicester',
      totalGross,
      totalNet,
      totalTax,
      totalNI,
      status: 'paid',
      employeePayments: [], // Simplified - not generating individual employee payments
    };
    payrollRuns.push(payrollRun);
    payrollCounter++;

    // Main payroll transaction
    const payrollTxn: BankTransaction = {
      id: `btxn-${String(bankTxnCounter).padStart(5, '0')}`,
      date: payrollDate,
      description: `Payroll - ${payrollDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`,
      amount: -totalNet,
      type: 'expense',
      category: 'Payroll',
      entity: 'Leicester',
      status: 'Settled',
      accountId: 'bank-002', // Payroll account
      sourceSystem: 'payroll',
    };
    bankTransactions.push(payrollTxn);
    bankTxnCounter++;

    // Employer NI transaction
    const niTxn: BankTransaction = {
      id: `btxn-${String(bankTxnCounter).padStart(5, '0')}`,
      date: payrollDate,
      description: `Employer NI - ${payrollDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`,
      amount: -totalNI,
      type: 'expense',
      category: 'Payroll',
      entity: 'Leicester',
      status: 'Settled',
      accountId: 'bank-002', // Payroll account
      sourceSystem: 'payroll',
    };
    bankTransactions.push(niTxn);
    bankTxnCounter++;

    // Pension contribution transaction
    const pensionTxn: BankTransaction = {
      id: `btxn-${String(bankTxnCounter).padStart(5, '0')}`,
      date: payrollDate,
      description: `Pension Contributions - ${payrollDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`,
      amount: -pensionAmount,
      type: 'expense',
      category: 'Payroll',
      entity: 'Leicester',
      status: 'Settled',
      accountId: 'bank-002', // Payroll account
      sourceSystem: 'payroll',
    };
    bankTransactions.push(pensionTxn);
    bankTxnCounter++;
  }

  // ============================================================================
  // STEP 6: COMPLETE - All transactions generated
  // ============================================================================
  
  // All historical paid bills and payroll have been generated above
  // Bank transactions were created alongside each paid bill/invoice
  // No additional transactions needed
  
  // Sort all bank transactions by date
  bankTransactions.sort((a, b) => a.date.getTime() - b.date.getTime());

  console.log('✅ ACCOUNTING SYSTEM COMPLETE (Section 2):');
  console.log(`   - ${invoices.length} Invoices (${invoices.filter(i => i.status === 'paid').length} paid, ${invoices.filter(i => i.status !== 'paid').length} outstanding)`);
  console.log(`   - ${bills.length} Bills (${bills.filter(b => b.status === 'paid').length} paid, ${bills.filter(b => b.status !== 'paid').length} outstanding)`);
  console.log(`   - ${payrollRuns.length} Payroll Runs`);
  console.log(`   - ${bankTransactions.length} Bank Transactions`);
  console.log(`   - Outstanding invoices total: £${invoices.filter(i => i.status !== 'paid').reduce((sum, i) => sum + i.amount, 0).toLocaleString()}`);
  console.log(`   - Outstanding bills total: £${bills.filter(b => b.status !== 'paid').reduce((sum, b) => sum + b.amount, 0).toLocaleString()}`);
  
  const historicalBillsTotal = bills.filter(b => b.status === 'paid').reduce((sum, b) => sum + b.amount, 0);
  const historicalPayrollTotal = payrollRuns.reduce((sum, pr) => sum + pr.totalGross + pr.totalNI, 0);
  const historicalExpensesTotal = historicalBillsTotal + historicalPayrollTotal;
  
  console.log(`   - Historical bills (90 days): £${historicalBillsTotal.toLocaleString()}`);
  console.log(`   - Historical payroll (90 days): £${historicalPayrollTotal.toLocaleString()}`);
  console.log(`   - Historical expenses TOTAL (90 days): £${historicalExpensesTotal.toLocaleString()}`);
  console.log(`   - Average monthly expenses: £${(historicalExpensesTotal / 3).toLocaleString()}`);
  console.log(`   - Expected coverage: £247,000 ÷ £${(historicalExpensesTotal / 3).toFixed(0)} = ${(247000 / (historicalExpensesTotal / 3)).toFixed(1)} months`);
  
  return {
    invoices,
    bills,
    payrollRuns,
    bankTransactions,
  };
}

// Generate the accounting system data
const ACCOUNTING_DATA = generateAccountingSystem();

// ============================================================================
// SYSTEM 2: PAYROLL SYSTEM
// ============================================================================

/**
 * PAYROLL SYSTEM GENERATION STRATEGY:
 * 
 * 1. Generate biweekly payroll runs (every other Friday)
 * 2. Historical: Oct 2023 → Nov 22, 2025 (all PAID)
 * 3. Future: Dec 6, 2025 and Dec 20, 2025 (SCHEDULED)
 * 4. Generate monthly PAYE/NIC payments (22nd of following month)
 * 5. Generate quarterly VAT payments (7th of month after quarter end)
 * 6. Link all to bank transactions
 * 
 * CALCULATIONS:
 * - 150 employees @ avg £35K = £5.25M annual
 * - Biweekly = £202K per run
 * - PAYE: ~20% of gross
 * - NIC: ~13% of gross (employer contribution)
 * - VAT: 20% on revenue (quarterly)
 */

interface PayrollData {
  payrollRuns: PayrollRun[];
  taxPayments: TaxPayment[];
  bankTransactions: BankTransaction[];
}

function generatePayrollSystem(existingBankTxns: BankTransaction[]): PayrollData {
  const payrollRuns: PayrollRun[] = [];
  const taxPayments: TaxPayment[] = [];
  const bankTransactions: BankTransaction[] = [];
  
  let payrollRunCounter = 1;
  let taxPaymentCounter = 1;
  let bankTxnCounter = existingBankTxns.length + 1;

  // ============================================================================
  // STEP 1: Generate BIWEEKLY PAYROLL RUNS
  // ============================================================================
  
  console.log('Generating biweekly payroll runs...');
  
  // Calculate total annual payroll
  const totalAnnualSalaries = EMPLOYEES.reduce((sum, emp) => sum + emp.salary, 0);
  const biweeklyPayrollTotal = totalAnnualSalaries / 26; // 26 pay periods per year
  
  console.log(`Total annual salaries: £${totalAnnualSalaries.toLocaleString()}`);
  console.log(`Biweekly payroll: £${Math.round(biweeklyPayrollTotal).toLocaleString()}`);
  
  // Generate payroll runs from Feb 2024 to May 2026
  // Start date: First Friday in February 2024 (Feb 2, 2024)
  let payrollDate = new Date(2024, 1, 2); // Feb 2, 2024 (Friday)
  const endDate = new Date(2026, 4, 1); // May 1, 2026
  
  while (payrollDate <= endDate) {
    const isPaid = payrollDate <= new Date(2026, 0, 30); // Jan 30, 2026 was last paid run
    const isScheduled = payrollDate > TODAY;
    
    // Calculate payroll for this run
    const employeePayments: EmployeePayment[] = [];
    let runTotalGross = 0;
    let runTotalNet = 0;
    let runTotalTax = 0;
    let runTotalNI = 0;
    
    // Group employees by entity for reporting
    const entitiesCovered = new Set<string>();
    
    EMPLOYEES.forEach(employee => {
      const biweeklySalary = employee.salary / 26;
      
      // Calculate tax (simplified UK PAYE - ~20% effective rate)
      const tax = biweeklySalary * 0.20;
      
      // Calculate NI (employee contribution - ~12%)
      const ni = biweeklySalary * 0.12;
      
      const netPay = biweeklySalary - tax - ni;
      
      employeePayments.push({
        employeeId: employee.id,
        entity: employee.entity,
        grossPay: biweeklySalary,
        netPay,
        tax,
        ni,
      });
      
      runTotalGross += biweeklySalary;
      runTotalNet += netPay;
      runTotalTax += tax;
      runTotalNI += ni;
      entitiesCovered.add(employee.entity);
    });
    
    const payrollRun: PayrollRun = {
      id: `payroll-${String(payrollRunCounter).padStart(4, '0')}`,
      runDate: new Date(payrollDate),
      payPeriodStart: addDays(payrollDate, -13), // 2 weeks before
      payPeriodEnd: new Date(payrollDate),
      entity: 'ALL', // Company-wide payroll
      totalGross: runTotalGross,
      totalNet: runTotalNet,
      totalTax: runTotalTax,
      totalNI: runTotalNI,
      status: isPaid ? 'paid' : 'approved',
      employeePayments,
    };
    
    payrollRuns.push(payrollRun);
    
    // Create bank transactions for PAID payroll runs
    if (isPaid) {
      // Main payroll payment (net pay to employees)
      const netPaymentTxn: BankTransaction = {
        id: `btxn-${String(bankTxnCounter).padStart(5, '0')}`,
        date: new Date(payrollDate),
        description: `Payroll - ${formatDate(payrollDate, undefined, { day: '2-digit', month: 'short', year: 'numeric' })} - Net Pay`,
        amount: -runTotalNet,
        type: 'expense',
        category: 'Payroll',
        entity: 'Leicester', // Paid from HQ
        status: 'Settled',
        accountId: 'bank-002', // Payroll account
        sourceSystem: 'payroll',
        sourceRecordId: payrollRun.id,
        payrollRunId: payrollRun.id,
      };
      
      bankTransactions.push(netPaymentTxn);
      bankTxnCounter++;
    } else if (isScheduled) {
      // Future scheduled payroll payment
      const netPaymentTxn: BankTransaction = {
        id: `btxn-${String(bankTxnCounter).padStart(5, '0')}`,
        date: new Date(payrollDate),
        description: `Payroll - ${formatDate(payrollDate, undefined, { day: '2-digit', month: 'short', year: 'numeric' })} - Net Pay`,
        amount: -runTotalNet,
        type: 'expense',
        category: 'Payroll',
        entity: 'Leicester',
        status: 'Outstanding',
        confidence: 'certain',
        accountId: 'bank-002',
        sourceSystem: 'payroll',
        sourceRecordId: payrollRun.id,
        payrollRunId: payrollRun.id,
      };
      
      bankTransactions.push(netPaymentTxn);
      bankTxnCounter++;
    }
    
    payrollRunCounter++;
    
    // Move to next biweekly period (add 14 days)
    payrollDate = addDays(payrollDate, 14);
  }

  // ============================================================================
  // STEP 2: Generate MONTHLY PAYE/NIC TAX PAYMENTS
  // ============================================================================
  
  console.log('Generating monthly PAYE/NIC tax payments...');
  
  // PAYE/NIC is paid monthly on the 22nd of the following month
  // Generate from Mar 2024 (for Feb 2024 payroll) to Jun 2026 (for May 2026 payroll)
  
  let taxMonth = new Date(2024, 1, 1); // Feb 2024
  const taxEndMonth = new Date(2026, 5, 1); // Jun 2026
  
  while (taxMonth <= taxEndMonth) {
    const paymentDate = new Date(taxMonth.getFullYear(), taxMonth.getMonth() + 1, 22); // 22nd of next month
    const isPaid = paymentDate <= TODAY;
    const isScheduled = paymentDate > TODAY;
    
    // Calculate tax for this month (sum of ~2 payroll runs)
    // Each month has roughly 2 biweekly payrolls
    const monthlyGrossPayroll = biweeklyPayrollTotal * 2;
    const monthlyPAYE = monthlyGrossPayroll * 0.20;
    const monthlyNIC = monthlyGrossPayroll * 0.13; // Employer NIC
    const totalTaxPayment = monthlyPAYE + monthlyNIC;
    
    const taxPayment: TaxPayment = {
      id: `tax-${String(taxPaymentCounter).padStart(4, '0')}`,
      type: 'PAYE',
      dueDate: paymentDate,
      amount: totalTaxPayment,
      period: `${taxMonth.getFullYear()}-${String(taxMonth.getMonth() + 1).padStart(2, '0')}`,
      status: isPaid ? 'paid' : 'scheduled',
      entity: 'Leicester',
    };
    
    taxPayments.push(taxPayment);
    
    // Create bank transaction
    if (isPaid) {
      const taxTxn: BankTransaction = {
        id: `btxn-${String(bankTxnCounter).padStart(5, '0')}`,
        date: paymentDate,
        description: `PAYE/NIC Payment - ${taxPayment.period}`,
        amount: -totalTaxPayment,
        type: 'expense',
        category: 'Tax',
        entity: 'Leicester',
        status: 'Settled',
        accountId: 'bank-001',
        sourceSystem: 'payroll',
        sourceRecordId: taxPayment.id,
      };
      
      bankTransactions.push(taxTxn);
      bankTxnCounter++;
    } else if (isScheduled) {
      const taxTxn: BankTransaction = {
        id: `btxn-${String(bankTxnCounter).padStart(5, '0')}`,
        date: paymentDate,
        description: `PAYE/NIC Payment - ${taxPayment.period}`,
        amount: -totalTaxPayment,
        type: 'expense',
        category: 'Tax',
        entity: 'Leicester',
        status: 'Outstanding',
        confidence: 'certain',
        accountId: 'bank-001',
        sourceSystem: 'payroll',
        sourceRecordId: taxPayment.id,
      };
      
      bankTransactions.push(taxTxn);
      bankTxnCounter++;
    }
    
    taxPaymentCounter++;
    
    // Move to next month
    taxMonth = addMonths(taxMonth, 1);
  }

  // ============================================================================
  // STEP 3: Generate QUARTERLY VAT PAYMENTS
  // ============================================================================
  
  console.log('Generating quarterly VAT payments...');
  
  // VAT quarters: Jan-Mar (due Apr 7), Apr-Jun (due Jul 7), Jul-Sep (due Oct 7), Oct-Dec (due Jan 7)
  // Generate from Q1 2024 to Q1 2026
  
  const vatQuarters = [
    { quarter: 'Q1 2024', period: 'Jan-Mar 2024', dueDate: new Date(2024, 3, 7) },
    { quarter: 'Q2 2024', period: 'Apr-Jun 2024', dueDate: new Date(2024, 6, 7) },
    { quarter: 'Q3 2024', period: 'Jul-Sep 2024', dueDate: new Date(2024, 9, 7) },
    { quarter: 'Q4 2024', period: 'Oct-Dec 2024', dueDate: new Date(2025, 0, 7) },
    { quarter: 'Q1 2025', period: 'Jan-Mar 2025', dueDate: new Date(2025, 3, 7) },
    { quarter: 'Q2 2025', period: 'Apr-Jun 2025', dueDate: new Date(2025, 6, 7) },
    { quarter: 'Q3 2025', period: 'Jul-Sep 2025', dueDate: new Date(2025, 9, 7) },
    { quarter: 'Q4 2025', period: 'Oct-Dec 2025', dueDate: new Date(2026, 0, 7) },
    { quarter: 'Q1 2026', period: 'Jan-Mar 2026', dueDate: new Date(2026, 3, 7) },
  ];
  
  vatQuarters.forEach(vat => {
    const isPaid = vat.dueDate <= TODAY;
    const isScheduled = vat.dueDate > TODAY;
    
    // Calculate VAT (20% of quarterly revenue)
    // Estimate quarterly revenue from invoices (~£600K per quarter)
    const quarterlyRevenue = 600000;
    const vatAmount = quarterlyRevenue * 0.20;
    
    const taxPayment: TaxPayment = {
      id: `tax-${String(taxPaymentCounter).padStart(4, '0')}`,
      type: 'VAT',
      dueDate: vat.dueDate,
      amount: vatAmount,
      period: vat.period,
      status: isPaid ? 'paid' : 'scheduled',
      entity: 'Leicester',
    };
    
    taxPayments.push(taxPayment);
    
    // Create bank transaction
    if (isPaid) {
      const vatTxn: BankTransaction = {
        id: `btxn-${String(bankTxnCounter).padStart(5, '0')}`,
        date: vat.dueDate,
        description: `VAT Payment - ${vat.period}`,
        amount: -vatAmount,
        type: 'expense',
        category: 'Tax',
        entity: 'Leicester',
        status: 'Settled',
        accountId: 'bank-001',
        sourceSystem: 'payroll',
        sourceRecordId: taxPayment.id,
      };
      
      bankTransactions.push(vatTxn);
      bankTxnCounter++;
    } else if (isScheduled) {
      const vatTxn: BankTransaction = {
        id: `btxn-${String(bankTxnCounter).padStart(5, '0')}`,
        date: vat.dueDate,
        description: `VAT Payment - ${vat.period}`,
        amount: -vatAmount,
        type: 'expense',
        category: 'Tax',
        entity: 'Leicester',
        status: 'Outstanding',
        confidence: 'certain',
        accountId: 'bank-001',
        sourceSystem: 'payroll',
        sourceRecordId: taxPayment.id,
      };
      
      bankTransactions.push(vatTxn);
      bankTxnCounter++;
    }
    
    taxPaymentCounter++;
  });

  // Sort bank transactions by date
  bankTransactions.sort((a, b) => a.date.getTime() - b.date.getTime());

  console.log('✅ PAYROLL SYSTEM COMPLETE:');
  console.log(`   - ${payrollRuns.length} Payroll Runs (${payrollRuns.filter(p => p.status === 'paid').length} paid, ${payrollRuns.filter(p => p.status === 'approved').length} scheduled)`);
  console.log(`   - ${taxPayments.length} Tax Payments (${taxPayments.filter(t => t.status === 'paid').length} paid, ${taxPayments.filter(t => t.status === 'scheduled').length} scheduled)`);
  console.log(`   - ${bankTransactions.length} Bank Transactions generated`);
  console.log(`   - Total annual payroll: £${totalAnnualSalaries.toLocaleString()}`);
  
  return {
    payrollRuns,
    taxPayments,
    bankTransactions,
  };
}

// Generate the payroll system data
const PAYROLL_DATA = generatePayrollSystem(ACCOUNTING_DATA.bankTransactions);

// ============================================================================
// SYSTEM 3: TIME RECORDING SYSTEM
// ============================================================================

// TimeSheet interface already defined at top of file

type UnbilledTimeSummary = {
  projectId: string;
  customerId: string;
  entity: string;
  totalHours: number;
  totalValue: number;
  oldestDate: Date;
  expectedInvoiceDate: Date;
  confidence: ConfidenceLevel;
}

function generateTimeRecordingSystem(existingInvoices: Invoice[]): {
  timeSheets: TimeSheet[];
  unbilledTime: UnbilledTimeSummary[];
  unbilledTimeSummary: UnbilledTimeSummary[];
  forecastInvoices: Invoice[];
} {
  const timeSheets: TimeSheet[] = [];
  const today = new Date(TODAY);
  const startDate = new Date(TODAY.getTime() - (60 * 24 * 60 * 60 * 1000)); // 60 days ago

  // Get billable employees (those with billableRate)
  const billableEmployees = EMPLOYEES.filter(emp => emp.billableRate && emp.billableRate > 0);
  
  // Get active projects
  const activeProjects = PROJECTS.filter(p => p.status === 'active');

  // Generate time sheets for last 60 days
  let timeSheetId = 1;
  
  for (let d = new Date(startDate); d <= today; d.setDate(d.getDate() + 1)) {
    const currentDate = new Date(d);
    const dayOfWeek = currentDate.getDay();
    
    // Skip weekends
    if (dayOfWeek === 0 || dayOfWeek === 6) continue;

    // Each billable employee works on projects
    for (const employee of billableEmployees) {
      // 80% chance employee worked this day (accounts for holidays, sick days)
      if (Math.random() > 0.8) continue;

      // Employee works on 1-2 projects per day
      const projectCount = Math.random() > 0.7 ? 2 : 1;
      const employeeProjects = activeProjects
        .filter(p => p.entity === employee.entity || Math.random() > 0.8) // Mostly local projects
        .sort(() => Math.random() - 0.5)
        .slice(0, projectCount);

      for (const project of employeeProjects) {
        const hours = projectCount === 1 
          ? 6 + Math.random() * 2 // 6-8 hours if only project
          : 3 + Math.random() * 2; // 3-5 hours if split between projects

        const value = hours * employee.billableRate!;
        const customer = CUSTOMERS.find(c => c.id === project.customerId)!;

        // Determine if this time has been billed yet
        // Time older than 30 days is likely billed (70% chance)
        // Time 15-30 days old is sometimes billed (40% chance)
        // Time less than 15 days old is rarely billed (10% chance)
        const daysOld = Math.floor((today.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
        let billed = false;
        let invoiceId: string | undefined;

        if (daysOld > 30 && Math.random() < 0.7) {
          billed = true;
          // Find or create invoice for this time
          const existingInvoice = existingInvoices.find(inv => 
            inv.customerId === customer.id && 
            inv.projectId === project.id &&
            inv.status === 'paid' &&
            Math.abs(inv.issueDate.getTime() - currentDate.getTime()) < 45 * 24 * 60 * 60 * 1000
          );
          if (existingInvoice) {
            invoiceId = existingInvoice.id;
          }
        } else if (daysOld > 15 && Math.random() < 0.4) {
          billed = true;
          const existingInvoice = existingInvoices.find(inv => 
            inv.customerId === customer.id && 
            inv.projectId === project.id &&
            Math.abs(inv.issueDate.getTime() - currentDate.getTime()) < 30 * 24 * 60 * 60 * 1000
          );
          if (existingInvoice) {
            invoiceId = existingInvoice.id;
          }
        } else if (daysOld > 5 && Math.random() < 0.1) {
          billed = true;
        }

        timeSheets.push({
          id: `TS-${String(timeSheetId).padStart(6, '0')}`,
          employeeId: employee.id,
          projectId: project.id,
          customerId: customer.id,
          entity: employee.entity,
          date: new Date(currentDate),
          hours: Math.round(hours * 10) / 10,
          billableRate: employee.billableRate!,
          value: Math.round(value * 100) / 100,
          billed,
          invoiceId,
        });

        timeSheetId++;
      }
    }
  }

  // Calculate unbilled time summary by project
  const unbilledByProject = new Map<string, TimeSheet[]>();
  
  for (const timeSheet of timeSheets) {
    if (!timeSheet.billed) {
      const key = timeSheet.projectId;
      if (!unbilledByProject.has(key)) {
        unbilledByProject.set(key, []);
      }
      unbilledByProject.get(key)!.push(timeSheet);
    }
  }

  const unbilledTimeSummary: UnbilledTimeSummary[] = [];
  
  for (const [projectId, sheets] of unbilledByProject.entries()) {
    const project = PROJECTS.find(p => p.id === projectId)!;
    const customer = CUSTOMERS.find(c => c.id === project.customerId)!;
    
    const totalHours = sheets.reduce((sum, s) => sum + s.hours, 0);
    const totalValue = sheets.reduce((sum, s) => sum + s.value, 0);
    const oldestDate = new Date(Math.min(...sheets.map(s => s.date.getTime())));
    
    // Expected invoice date: 30 days after oldest unbilled time
    const expectedInvoiceDate = new Date(oldestDate);
    expectedInvoiceDate.setDate(expectedInvoiceDate.getDate() + 30);

    unbilledTimeSummary.push({
      projectId: project.id,
      customerId: customer.id,
      entity: project.entity,
      totalHours: Math.round(totalHours * 10) / 10,
      totalValue: Math.round(totalValue),
      oldestDate,
      expectedInvoiceDate,
      confidence: 'probable' as ConfidenceLevel, // Will definitely be billed
    });
  }

  // Generate forecast invoices from unbilled time
  const forecastInvoices: Invoice[] = [];
  let forecastInvoiceId = 1000; // Start after existing invoices

  for (const summary of unbilledTimeSummary) {
    const customer = CUSTOMERS.find(c => c.id === summary.customerId)!;
    const project = PROJECTS.find(p => p.id === summary.projectId)!;
    
    // Create invoice for unbilled time
    const issueDate = new Date(summary.expectedInvoiceDate);
    const dueDate = new Date(issueDate);
    dueDate.setDate(dueDate.getDate() + customer.paymentTerms);

    forecastInvoices.push({
      id: `INV-${String(forecastInvoiceId).padStart(6, '0')}`,
      invoiceNumber: `INV-2026-${String(forecastInvoiceId).padStart(4, '0')}`,
      customerId: customer.id,
      projectId: project.id,
      entity: summary.entity,
      amount: summary.totalValue,
      issueDate,
      dueDate,
      status: 'draft', // Not yet issued
      confidence: 'probable' as ConfidenceLevel,
      sourceSystem: 'time-recording',
      sourceRecordId: summary.projectId,
    });

    forecastInvoiceId++;
  }

  return {
    timeSheets,
    unbilledTime: unbilledTimeSummary,
    unbilledTimeSummary,
    forecastInvoices,
  };
}

// Generate the time recording system data
const TIME_RECORDING_DATA = generateTimeRecordingSystem(ACCOUNTING_DATA.invoices);

// ============================================================================
// SYSTEM 4: PURCHASE & SALES ORDERS
// ============================================================================

function generatePurchaseAndSalesOrders(
  existingInvoices: Invoice[],
  existingBills: Bill[]
): {
  purchaseOrders: PurchaseOrder[];
  salesOrders: SalesOrder[];
  forecastBills: Bill[];
  forecastInvoices: Invoice[];
} {
  console.log('\n=== GENERATING SYSTEM 4: PURCHASE & SALES ORDERS ===');
  
  const purchaseOrders: PurchaseOrder[] = [];
  const salesOrders: SalesOrder[] = [];
  const forecastBills: Bill[] = [];
  const forecastInvoices: Invoice[] = [];
  
  let poCounter = 1;
  let soCounter = 1;
  let forecastBillCounter = existingBills.length + 1;
  let forecastInvoiceCounter = existingInvoices.length + 1;
  
  // ============================================================================
  // STEP 1: Generate PURCHASE ORDERS (8-12 active POs)
  // ============================================================================
  
  console.log('Generating purchase orders...');
  
  const numPOs = 8 + Math.floor(Math.random() * 5); // 8-12 POs
  const materialSuppliers = SUPPLIERS.filter(s => s.category === 'Materials');
  const equipmentSuppliers = SUPPLIERS.filter(s => s.category === 'Equipment');
  const subcontractorSuppliers = SUPPLIERS.filter(s => s.category === 'Subcontractor');
  
  // Status distribution: 30% sent, 50% received, 20% invoiced
  const poStatuses: Array<'sent' | 'received' | 'invoiced'> = [
    ...Array(Math.ceil(numPOs * 0.3)).fill('sent'),
    ...Array(Math.ceil(numPOs * 0.5)).fill('received'),
    ...Array(Math.floor(numPOs * 0.2)).fill('invoiced'),
  ];
  
  for (let i = 0; i < numPOs; i++) {
    // Select supplier category
    const categoryRoll = Math.random();
    let supplier: Supplier;
    let category: TransactionCategory;
    
    if (categoryRoll < 0.5) {
      // 50% Materials
      supplier = materialSuppliers[Math.floor(Math.random() * materialSuppliers.length)];
      category = 'Materials';
    } else if (categoryRoll < 0.75) {
      // 25% Equipment
      supplier = equipmentSuppliers[Math.floor(Math.random() * equipmentSuppliers.length)];
      category = 'Equipment Rental';
    } else {
      // 25% Subcontractors
      supplier = subcontractorSuppliers[Math.floor(Math.random() * subcontractorSuppliers.length)];
      category = 'Subcontractors';
    }
    
    // Select project (prefer active projects in supplier's entity)
    const entityProjects = PROJECTS.filter(p => 
      p.entity === supplier.primaryEntity && 
      p.status === 'active'
    );
    const project = entityProjects.length > 0
      ? entityProjects[Math.floor(Math.random() * entityProjects.length)]
      : PROJECTS.filter(p => p.status === 'active')[Math.floor(Math.random() * PROJECTS.filter(p => p.status === 'active').length)];
    
    // Order date: November-December 2025
    const orderDate = new Date(2025, 10 + Math.floor(Math.random() * 2), 1 + Math.floor(Math.random() * 28));
    
    // Expected delivery: 2-8 weeks after order
    const deliveryWeeks = 2 + Math.floor(Math.random() * 7);
    const expectedDeliveryDate = addDays(orderDate, deliveryWeeks * 7);
    
    // Amount: £5K-£25K
    const amount = Math.round((5000 + Math.random() * 20000) / 50) * 50;
    
    // Status
    const status = poStatuses[i % poStatuses.length];
    
    const po: PurchaseOrder = {
      id: `PO-${String(poCounter).padStart(4, '0')}`,
      poNumber: `PO-2025-${String(poCounter).padStart(4, '0')}`,
      supplierId: supplier.id,
      projectId: project.id,
      entity: project.entity,
      amount,
      orderDate,
      expectedDeliveryDate,
      status,
      category,
    };
    
    // If status is 'invoiced', link to an existing bill
    if (status === 'invoiced') {
      // Find a matching bill (or create forecast)
      const matchingBill = existingBills.find(b => 
        b.supplierId === supplier.id && 
        b.amount === amount &&
        !b.sourceRecordId // Not already linked
      );
      
      if (matchingBill) {
        po.billId = matchingBill.id;
        matchingBill.sourceSystem = 'purchase-order';
        matchingBill.sourceRecordId = po.id;
      }
    }
    
    // If status is 'sent' or 'received', create forecast bill
    if (status === 'sent' || status === 'received') {
      const billDate = status === 'sent' 
        ? expectedDeliveryDate 
        : addDays(expectedDeliveryDate, 7); // Invoice 7 days after delivery
      
      const dueDate = addDays(billDate, supplier.paymentTerms);
      
      const forecastBill: Bill = {
        id: `BILL-${String(forecastBillCounter++).padStart(4, '0')}`,
        billNumber: `BILL-2026-${String(forecastBillCounter).padStart(4, '0')}`,
        supplierId: supplier.id,
        projectId: project.id,
        entity: project.entity,
        amount,
        billDate,
        dueDate,
        status: 'received',
        category: category as TransactionCategory,
        sourceSystem: 'purchase-order',
        sourceRecordId: po.id,
      };
      
      forecastBills.push(forecastBill);
      po.billId = forecastBill.id;
    }
    
    purchaseOrders.push(po);
    poCounter++;
  }
  
  console.log(`Generated ${purchaseOrders.length} purchase orders`);
  console.log(`  - ${purchaseOrders.filter(po => po.status === 'sent').length} sent (awaiting delivery)`);
  console.log(`  - ${purchaseOrders.filter(po => po.status === 'received').length} received (awaiting invoice)`);
  console.log(`  - ${purchaseOrders.filter(po => po.status === 'invoiced').length} invoiced (converted to bills)`);
  console.log(`Generated ${forecastBills.length} forecast bills from POs`);
  
  // ============================================================================
  // STEP 2: Generate SALES ORDERS (15-20 active SOs)
  // ============================================================================
  
  console.log('Generating sales orders...');
  
  const numSOs = 15 + Math.floor(Math.random() * 6); // 15-20 SOs
  
  // Status distribution: 40% confirmed, 40% delivered, 20% invoiced
  const soStatuses: Array<'confirmed' | 'delivered' | 'invoiced'> = [
    ...Array(Math.ceil(numSOs * 0.4)).fill('confirmed'),
    ...Array(Math.ceil(numSOs * 0.4)).fill('delivered'),
    ...Array(Math.floor(numSOs * 0.2)).fill('invoiced'),
  ];
  
  for (let i = 0; i < numSOs; i++) {
    // Select customer
    const customer = CUSTOMERS[Math.floor(Math.random() * CUSTOMERS.length)];
    
    // Select project (prefer active projects for this customer)
    const customerProjects = PROJECTS.filter(p => 
      p.customerId === customer.id && 
      p.status === 'active'
    );
    const project = customerProjects.length > 0
      ? customerProjects[Math.floor(Math.random() * customerProjects.length)]
      : PROJECTS.filter(p => p.status === 'active')[Math.floor(Math.random() * PROJECTS.filter(p => p.status === 'active').length)];
    
    // Order date: October-December 2025
    const orderDate = new Date(2025, 9 + Math.floor(Math.random() * 3), 1 + Math.floor(Math.random() * 28));
    
    // Expected delivery: 3-12 weeks after order
    const deliveryWeeks = 3 + Math.floor(Math.random() * 10);
    const expectedDeliveryDate = addDays(orderDate, deliveryWeeks * 7);
    
    // Amount: £10K-£40K
    const amount = Math.round((10000 + Math.random() * 30000) / 100) * 100;
    
    // Status
    const status = soStatuses[i % soStatuses.length];
    
    const so: SalesOrder = {
      id: `SO-${String(soCounter).padStart(4, '0')}`,
      soNumber: `SO-2025-${String(soCounter).padStart(4, '0')}`,
      customerId: customer.id,
      projectId: project.id,
      entity: project.entity,
      amount,
      orderDate,
      expectedDeliveryDate,
      status,
    };
    
    // If status is 'invoiced', link to an existing invoice
    if (status === 'invoiced') {
      // Find a matching invoice (or create forecast)
      const matchingInvoice = existingInvoices.find(inv => 
        inv.customerId === customer.id && 
        inv.amount === amount &&
        !inv.sourceRecordId // Not already linked
      );
      
      if (matchingInvoice) {
        so.invoiceId = matchingInvoice.id;
        matchingInvoice.sourceSystem = 'sales-order';
        matchingInvoice.sourceRecordId = so.id;
      }
    }
    
    // If status is 'confirmed' or 'delivered', create forecast invoice
    if (status === 'confirmed' || status === 'delivered') {
      const issueDate = status === 'confirmed' 
        ? expectedDeliveryDate 
        : addDays(expectedDeliveryDate, 3); // Invoice 3 days after delivery
      
      const dueDate = addDays(issueDate, customer.paymentTerms);
      
      // Calculate confidence based on customer reliability and status
      let confidence: ConfidenceLevel = 'probable';
      if (status === 'delivered') {
        confidence = customer.paymentReliability > 0.9 ? 'certain' : 'probable';
      }
      
      const forecastInvoice: Invoice = {
        id: `INV-${String(forecastInvoiceCounter++).padStart(4, '0')}`,
        invoiceNumber: `INV-2026-${String(forecastInvoiceCounter).padStart(4, '0')}`,
        customerId: customer.id,
        projectId: project.id,
        entity: project.entity,
        amount,
        issueDate,
        dueDate,
        status: 'sent',
        confidence,
        sourceSystem: 'sales-order',
        sourceRecordId: so.id,
      };
      
      forecastInvoices.push(forecastInvoice);
      so.invoiceId = forecastInvoice.id;
    }
    
    salesOrders.push(so);
    soCounter++;
  }
  
  console.log(`Generated ${salesOrders.length} sales orders`);
  console.log(`  - ${salesOrders.filter(so => so.status === 'confirmed').length} confirmed (not yet delivered)`);
  console.log(`  - ${salesOrders.filter(so => so.status === 'delivered').length} delivered (awaiting invoice)`);
  console.log(`  - ${salesOrders.filter(so => so.status === 'invoiced').length} invoiced (converted to invoices)`);
  console.log(`Generated ${forecastInvoices.length} forecast invoices from SOs`);
  
  return {
    purchaseOrders,
    salesOrders,
    forecastBills,
    forecastInvoices,
  };
}

// Generate the purchase & sales orders system data
const ORDERS_DATA = generatePurchaseAndSalesOrders(
  ACCOUNTING_DATA.invoices,
  ACCOUNTING_DATA.bills
);

// ============================================================================
// SYSTEM 5: CRM PIPELINE
// ============================================================================

function generateCRMPipeline(
  existingInvoices: Invoice[]
): {
  opportunities: Opportunity[];
  forecastInvoices: Invoice[];
  pipelineValue: {
    total: number;
    weighted: number;
    byStage: Record<string, { count: number; total: number; weighted: number }>;
  };
} {
  console.log('\n=== GENERATING SYSTEM 5: CRM PIPELINE ===');
  
  const opportunities: Opportunity[] = [];
  const forecastInvoices: Invoice[] = [];
  
  let oppCounter = 1;
  let forecastInvoiceCounter = existingInvoices.length + 1;
  
  // Pipeline value tracking
  let totalPipelineValue = 0;
  let weightedPipelineValue = 0;
  const stageStats: Record<string, { count: number; total: number; weighted: number }> = {
    prospecting: { count: 0, total: 0, weighted: 0 },
    qualification: { count: 0, total: 0, weighted: 0 },
    proposal: { count: 0, total: 0, weighted: 0 },
    negotiation: { count: 0, total: 0, weighted: 0 },
    closing: { count: 0, total: 0, weighted: 0 },
  };
  
  // ============================================================================
  // STEP 1: Generate OPPORTUNITIES (12-18 active deals)
  // ============================================================================
  
  console.log('Generating CRM opportunities...');
  
  const numOpportunities = 12 + Math.floor(Math.random() * 7); // 12-18 opportunities
  
  // Stage distribution with probabilities
  const stageConfig: Array<{
    stage: 'prospecting' | 'qualification' | 'proposal' | 'negotiation' | 'closing';
    probability: number;
    weight: number; // Distribution weight
  }> = [
    { stage: 'prospecting', probability: 0.10, weight: 0.15 },  // 15% of deals
    { stage: 'qualification', probability: 0.25, weight: 0.25 }, // 25% of deals
    { stage: 'proposal', probability: 0.50, weight: 0.30 },      // 30% of deals
    { stage: 'negotiation', probability: 0.75, weight: 0.20 },   // 20% of deals
    { stage: 'closing', probability: 0.90, weight: 0.10 },       // 10% of deals
  ];
  
  // Create stage distribution array
  const stageDistribution: typeof stageConfig[0][] = [];
  stageConfig.forEach(config => {
    const count = Math.ceil(numOpportunities * config.weight);
    for (let i = 0; i < count; i++) {
      stageDistribution.push(config);
    }
  });
  
  for (let i = 0; i < numOpportunities; i++) {
    // Select stage
    const stageInfo = stageDistribution[i % stageDistribution.length];
    const stage = stageInfo.stage;
    const probability = stageInfo.probability;
    
    // Determine deal size for construction projects
    const dealSizeRoll = Math.random();
    let dealValue: number;
    
    if (dealSizeRoll < 0.20) {
      // 20% Large projects (£200K-£500K)
      dealValue = Math.round((200000 + Math.random() * 300000) / 1000) * 1000;
    } else if (dealSizeRoll < 0.50) {
      // 30% Medium projects (£100K-£200K)
      dealValue = Math.round((100000 + Math.random() * 100000) / 1000) * 1000;
    } else {
      // 50% Small projects (£50K-£100K)
      dealValue = Math.round((50000 + Math.random() * 50000) / 1000) * 1000;
    }
    
    const entity = 'Leicester'; // All deals in Leicester
    
    // Select customer (70% existing, 30% new prospects)
    const customer = Math.random() < 0.7
      ? CUSTOMERS[Math.floor(Math.random() * CUSTOMERS.length)]
      : CUSTOMERS[Math.floor(Math.random() * CUSTOMERS.length)];
    
    // Expected close date: December 2025 - June 2026
    // Earlier stages have later close dates
    const monthsOut = stage === 'closing' ? 0 :
                     stage === 'negotiation' ? 1 :
                     stage === 'proposal' ? 2 :
                     stage === 'qualification' ? 3 : 4;
    
    const expectedCloseDate = new Date(2025, 11 + monthsOut, 1 + Math.floor(Math.random() * 28));
    
    // Generate deal name
    const dealTypes = [
      'Office Renovation',
      'Warehouse Construction',
      'Retail Fit-Out',
      'Industrial Expansion',
      'Commercial Refurbishment',
      'Infrastructure Project',
      'Residential Development',
      'Mixed-Use Development',
      'Factory Extension',
      'Distribution Centre',
    ];
    const dealType = dealTypes[Math.floor(Math.random() * dealTypes.length)];
    const dealName = `${customer.name} - ${dealType}`;
    
    // Calculate confidence based on probability
    let confidence: ConfidenceLevel;
    if (probability >= 0.75) {
      confidence = 'probable';
    } else if (probability >= 0.50) {
      confidence = 'possible';
    } else {
      confidence = 'possible'; // Low probability still possible
    }
    
    const opportunity: Opportunity = {
      id: `OPP-${String(oppCounter).padStart(4, '0')}`,
      dealName,
      customerId: customer.id,
      entity,
      value: dealValue,
      probability,
      stage,
      expectedCloseDate,
      confidence,
    };
    
    opportunities.push(opportunity);
    oppCounter++;
    
    // Track pipeline statistics
    totalPipelineValue += dealValue;
    weightedPipelineValue += dealValue * probability;
    stageStats[stage].count++;
    stageStats[stage].total += dealValue;
    stageStats[stage].weighted += dealValue * probability;
    
    // ============================================================================
    // STEP 2: Create FORECAST INVOICES for high-probability deals (≥50%)
    // ============================================================================
    
    if (probability >= 0.50) {
      // Invoice date = close date + 30 days (project start + first milestone)
      const issueDate = addDays(expectedCloseDate, 30);
      const dueDate = addDays(issueDate, customer.paymentTerms);
      
      // Find or create project for this opportunity
      const existingProject = PROJECTS.find(p => 
        p.customerId === customer.id && 
        p.entity === entity &&
        p.status === 'active'
      );
      
      const projectId = existingProject?.id || `PROJ-${String(oppCounter).padStart(3, '0')}`;
      
      const forecastInvoice: Invoice = {
        id: `INV-${String(forecastInvoiceCounter++).padStart(4, '0')}`,
        invoiceNumber: `INV-2026-${String(forecastInvoiceCounter).padStart(4, '0')}`,
        customerId: customer.id,
        projectId,
        entity,
        amount: dealValue,
        issueDate,
        dueDate,
        status: 'draft', // Not yet won
        confidence,
        sourceSystem: 'crm',
        sourceRecordId: opportunity.id,
      };
      
      forecastInvoices.push(forecastInvoice);
      opportunity.invoiceId = forecastInvoice.id;
    }
  }
  
  console.log(`Generated ${opportunities.length} CRM opportunities`);
  console.log(`  - ${stageStats.prospecting.count} prospecting (10% probability)`);
  console.log(`  - ${stageStats.qualification.count} qualification (25% probability)`);
  console.log(`  - ${stageStats.proposal.count} proposal (50% probability)`);
  console.log(`  - ${stageStats.negotiation.count} negotiation (75% probability)`);
  console.log(`  - ${stageStats.closing.count} closing (90% probability)`);
  console.log(`\nPipeline Value:`);
  console.log(`  - Total: £${(totalPipelineValue / 1000).toFixed(0)}K`);
  console.log(`  - Weighted: £${(weightedPipelineValue / 1000).toFixed(0)}K`);
  console.log(`Generated ${forecastInvoices.length} forecast invoices from CRM (≥50% probability)`);
  
  return {
    opportunities,
    forecastInvoices,
    pipelineValue: {
      total: totalPipelineValue,
      weighted: weightedPipelineValue,
      byStage: stageStats,
    },
  };
}

// Generate the CRM pipeline system data
const CRM_DATA = generateCRMPipeline(
  [
    ...ACCOUNTING_DATA.invoices,
    ...TIME_RECORDING_DATA.forecastInvoices,
    ...ORDERS_DATA.forecastInvoices,
  ]
);

// ============================================================================
// SYSTEM 6: BUDGET SYSTEM
// ============================================================================

function generateBudgetSystem(): {
  monthlyBudgets: MonthlyBudget[];
  budgetSummary: {
    totalMonths: number;
    totalRevenue: number;
    totalExpenses: number;
    averageMonthlyRevenue: number;
    averageMonthlyExpenses: number;
    byEntity: Record<string, { revenue: number; expenses: number }>;
  };
} {
  console.log('\n=== GENERATING SYSTEM 6: BUDGET SYSTEM ===');
  
  const monthlyBudgets: MonthlyBudget[] = [];
  
  // ============================================================================
  // STEP 1: Define BUDGET PARAMETERS by entity
  // ============================================================================
  
  // Monthly revenue target (based on annual turnover of £25M)
  const entityMonthlyRevenue: Record<string, number> = {
    'Leicester': MONTHLY_REVENUE, // £2,083,333 per month
  };
  
  // Expense ratios by category (as % of revenue)
  // Based on £316,667 monthly expenses / £2,083,333 monthly revenue = 15.2%
  const expenseRatios = {
    'Materials': 0.0576,         // £120K/month = 5.76% of revenue
    'Subcontractors': 0.0384,    // £80K/month = 3.84% of revenue
    'Equipment': 0.0120,         // £25K/month = 1.20% of revenue
    'Payroll': 0.0298,           // £62K/month = 2.98% of revenue
    'Employer NI': 0.0036,       // £7.5K/month = 0.36% of revenue
    'Pension': 0.0017,           // £3.5K/month = 0.17% of revenue
    'Rent': 0.0017,              // £3.5K/month = 0.17% of revenue
    'Utilities': 0.0006,         // £1.2K/month = 0.06% of revenue
    'Insurance': 0.0010,         // £2K/month = 0.10% of revenue
    'Vehicle Costs': 0.0012,     // £2.5K/month = 0.12% of revenue
    'Software': 0.0004,          // £850/month = 0.04% of revenue
    'Professional Services': 0.0007, // £1.5K/month = 0.07% of revenue
    'Marketing': 0.0004,         // £800/month = 0.04% of revenue
    'Sundries': 0.0012,          // £2.5K/month = 0.12% of revenue
    'VAT': 0.0031,               // £6.4K/month = 0.31% of revenue
    'Corporation Tax': 0.0018,   // £3.75K/month = 0.18% of revenue
  };
  
  // Total expense ratio: 15.2% (construction has low operating expenses relative to revenue due to high materials/subcontractor costs being project-specific)
  
  // Annual growth rate: 5-8%
  const annualGrowthRate = 0.06; // 6% average
  const monthlyGrowthRate = Math.pow(1 + annualGrowthRate, 1/12) - 1; // ~0.487% per month
  
  // ============================================================================
  // STEP 2: Generate MONTHLY BUDGETS (March 2026 → December 2028)
  // ============================================================================
  
  console.log('Generating monthly budgets...');
  
  const startDate = new Date(2026, 2, 1); // March 2026
  const endDate = new Date(2028, 11, 31); // December 2028
  
  let currentDate = new Date(startDate);
  let budgetCounter = 1;
  
  // Track totals for summary
  let totalRevenue = 0;
  let totalExpenses = 0;
  const entityTotals: Record<string, { revenue: number; expenses: number }> = {};
  
  ENTITIES.forEach(entity => {
    entityTotals[entity.name] = { revenue: 0, expenses: 0 };
  });
  
  while (currentDate <= endDate) {
    const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    const monthsFromStart = (currentDate.getFullYear() - 2026) * 12 + currentDate.getMonth() - 2;
    
    // Calculate growth multiplier
    const growthMultiplier = Math.pow(1 + monthlyGrowthRate, monthsFromStart);
    
    // Generate budget for each entity
    ENTITIES.forEach(entity => {
      const baseRevenue = entityMonthlyRevenue[entity.name];
      const budgetedRevenue = Math.round(baseRevenue * growthMultiplier);
      
      // Add some randomness (±5%) to make it realistic
      const revenueVariance = 1 + (Math.random() - 0.5) * 0.1; // ±5%
      const finalRevenue = Math.round(budgetedRevenue * revenueVariance / 1000) * 1000;
      
      // Calculate expenses by category
      let totalEntityExpenses = 0;
      
      Object.entries(expenseRatios).forEach(([category, ratio]) => {
        const budgetedExpense = Math.round(finalRevenue * ratio);
        
        // Add some randomness (±10%) to expenses
        const expenseVariance = 1 + (Math.random() - 0.5) * 0.2; // ±10%
        const finalExpense = Math.round(budgetedExpense * expenseVariance / 100) * 100;
        
        totalEntityExpenses += finalExpense;
        
        // Create budget record for this category
        const budget: MonthlyBudget = {
          id: `BUDGET-${String(budgetCounter++).padStart(5, '0')}`,
          month: monthKey,
          entity: entity.name,
          category: category as TransactionCategory,
          budgetedIncome: category === 'Revenue' ? finalRevenue : 0,
          budgetedExpense: category !== 'Revenue' ? finalExpense : 0,
          confidence: 'possible', // Budget is always low confidence
        };
        
        monthlyBudgets.push(budget);
      });
      
      // Create revenue budget record
      const revenueBudget: MonthlyBudget = {
        id: `BUDGET-${String(budgetCounter++).padStart(5, '0')}`,
        month: monthKey,
        entity: entity.name,
        category: 'Revenue',
        budgetedIncome: finalRevenue,
        budgetedExpense: 0,
        confidence: 'possible',
      };
      
      monthlyBudgets.push(revenueBudget);
      
      // Track totals
      totalRevenue += finalRevenue;
      totalExpenses += totalEntityExpenses;
      entityTotals[entity.name].revenue += finalRevenue;
      entityTotals[entity.name].expenses += totalEntityExpenses;
    });
    
    // Move to next month
    currentDate.setMonth(currentDate.getMonth() + 1);
  }
  
  const totalMonths = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 30));
  
  console.log(`Generated ${monthlyBudgets.length} monthly budget records`);
  console.log(`  - Period: March 2026 → December 2028 (${totalMonths} months)`);
  console.log(`  - Total budgeted revenue: £${(totalRevenue / 1000000).toFixed(1)}M`);
  console.log(`  - Total budgeted expenses: £${(totalExpenses / 1000000).toFixed(1)}M`);
  console.log(`  - Average monthly revenue: £${(totalRevenue / totalMonths / 1000).toFixed(0)}K`);
  console.log(`  - Average monthly expenses: £${(totalExpenses / totalMonths / 1000).toFixed(0)}K`);
  console.log(`  - Profit margin: ${((1 - totalExpenses / totalRevenue) * 100).toFixed(1)}%`);
  console.log(`  - Annual growth rate: ${(annualGrowthRate * 100).toFixed(1)}%`);
  
  return {
    monthlyBudgets,
    budgetSummary: {
      totalMonths,
      totalRevenue,
      totalExpenses,
      averageMonthlyRevenue: totalRevenue / totalMonths,
      averageMonthlyExpenses: totalExpenses / totalMonths,
      byEntity: entityTotals,
    },
  };
}

// Generate the budget system data
const BUDGET_DATA = generateBudgetSystem();

// ============================================================================
// MASTER EXPORT: Combine all 6 systems
// ============================================================================

export interface FinancialSystemsData {
  // Master Data
  entities: Entity[];
  customers: Customer[];
  suppliers: Supplier[];
  employees: Employee[];
  projects: Project[];
  bankAccounts: BankAccount[];
  
  // System 1: Accounting
  invoices: Invoice[];
  bills: Bill[];
  bankTransactions: BankTransaction[];
  
  // System 2: Payroll
  payrollRuns: PayrollRun[];
  taxPayments: TaxPayment[];
  
  // System 3: Time Recording
  timeSheets: TimeSheet[];
  unbilledTime: UnbilledTime[];
  
  // System 4: Purchase & Sales Orders
  purchaseOrders: PurchaseOrder[];
  salesOrders: SalesOrder[];
  
  // System 5: CRM Pipeline
  opportunities: Opportunity[];
  
  // System 6: Budget
  monthlyBudgets: MonthlyBudget[];
  
  // Metadata
  metadata: {
    generatedAt: Date;
    baselineDate: Date;
    totalTransactions: number;
    totalInvoices: number;
    totalBills: number;
    totalEmployees: number;
    totalProjects: number;
    totalOpportunities: number;
    totalBudgetMonths: number;
  };
}

/**
 * Generate complete financial systems data
 * This is the main entry point for the 6-system architecture
 */
export function generateFinancialData(): FinancialSystemsData {
  console.log('\n╔════════════════════════════════════════════════════════════════╗');
  console.log('║  FINANCIAL SYSTEMS DATA GENERATION                             ║');
  console.log('║  6-System Architecture with Referential Integrity              ║');
  console.log('╚════════════════════════════════════════════════════════════════╝');
  
  // Combine all invoices from all systems
  const allInvoices = [
    ...ACCOUNTING_DATA.invoices,
    ...TIME_RECORDING_DATA.forecastInvoices,
    ...ORDERS_DATA.forecastInvoices,
    ...CRM_DATA.forecastInvoices,
  ];
  
  // Combine all bills from all systems
  const allBills = [
    ...ACCOUNTING_DATA.bills,
    ...ORDERS_DATA.forecastBills,
  ];
  
  // Combine all bank transactions from all systems
  const allBankTransactions = [
    ...ACCOUNTING_DATA.bankTransactions,
    ...PAYROLL_DATA.bankTransactions,
  ];
  
  console.log('\n=== FINAL DATA SUMMARY ===');
  console.log(`Total Entities: ${ENTITIES.length}`);
  console.log(`Total Customers: ${CUSTOMERS.length}`);
  console.log(`Total Suppliers: ${SUPPLIERS.length}`);
  console.log(`Total Employees: ${EMPLOYEES.length}`);
  console.log(`Total Projects: ${PROJECTS.length}`);
  console.log(`Total Bank Accounts: ${BANK_ACCOUNTS.length}`);
  console.log(`\nTotal Invoices: ${allInvoices.length}`);
  console.log(`Total Bills: ${allBills.length}`);
  console.log(`Total Bank Transactions: ${allBankTransactions.length}`);
  console.log(`Total Payroll Runs: ${PAYROLL_DATA.payrollRuns.length}`);
  console.log(`Total Tax Payments: ${PAYROLL_DATA.taxPayments.length}`);
  console.log(`Total Time Sheets: ${TIME_RECORDING_DATA.timeSheets.length}`);
  console.log(`Total Unbilled Time Records: ${TIME_RECORDING_DATA.unbilledTime.length}`);
  console.log(`Total Purchase Orders: ${ORDERS_DATA.purchaseOrders.length}`);
  console.log(`Total Sales Orders: ${ORDERS_DATA.salesOrders.length}`);
  console.log(`Total Opportunities: ${CRM_DATA.opportunities.length}`);
  console.log(`Total Budget Records: ${BUDGET_DATA.monthlyBudgets.length}`);
  console.log('\n✅ All 6 systems generated successfully!\n');
  
  return {
    // Master Data
    entities: ENTITIES,
    customers: CUSTOMERS,
    suppliers: SUPPLIERS,
    employees: EMPLOYEES,
    projects: PROJECTS,
    bankAccounts: BANK_ACCOUNTS,
    
    // System 1: Accounting
    invoices: allInvoices,
    bills: allBills,
    bankTransactions: allBankTransactions,
    
    // System 2: Payroll
    payrollRuns: [
      ...ACCOUNTING_DATA.payrollRuns, // Section 2 payroll runs (last 90 days) - ONLY THESE
      // ...PAYROLL_DATA.payrollRuns, // DISABLED - Old payroll has unrealistic amounts
    ],
    taxPayments: PAYROLL_DATA.taxPayments,
    
    // System 3: Time Recording
    timeSheets: TIME_RECORDING_DATA.timeSheets,
    unbilledTime: TIME_RECORDING_DATA.unbilledTime,
    
    // System 4: Purchase & Sales Orders
    purchaseOrders: ORDERS_DATA.purchaseOrders,
    salesOrders: ORDERS_DATA.salesOrders,
    
    // System 5: CRM Pipeline
    opportunities: CRM_DATA.opportunities,
    
    // System 6: Budget
    monthlyBudgets: BUDGET_DATA.monthlyBudgets,
    
    // Metadata
    metadata: {
      generatedAt: new Date(),
      baselineDate: TODAY,
      totalTransactions: allBankTransactions.length,
      totalInvoices: allInvoices.length,
      totalBills: allBills.length,
      totalEmployees: EMPLOYEES.length,
      totalProjects: PROJECTS.length,
      totalOpportunities: CRM_DATA.opportunities.length,
      totalBudgetMonths: BUDGET_DATA.budgetSummary.totalMonths,
    },
  };
}

/**
 * Singleton instance of financial data
 * Generated once and reused throughout the application
 * Used by metrics service to calculate prioritised metrics
 */
console.log('🏗️ Generating financial data with TODAY:', TODAY.toISOString());
export const financialData = generateFinancialData();
console.log('🏗️ Financial data generated:', {
  invoiceCount: financialData.invoices?.length || 0,
  billCount: financialData.bills?.length || 0,
  bankAccountCount: financialData.bankAccounts?.length || 0,
  firstInvoice: financialData.invoices?.[0],
  firstBill: financialData.bills?.[0]
});
